import { useState, useEffect, useLayoutEffect, useRef, useDeferredValue, startTransition } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import { 
  Upload, 
  Search, 
  Users, 
  Calendar,
  FileSpreadsheet,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  X,
  Pencil,
  RefreshCw,
  Plus,
  Settings2,
  Euro,
  FileText,
  CreditCard,
  Link2,
  Phone,
  RotateCcw,
  TrendingUp,
  Package,
  ChevronDown,
  Check,
  ListChecks,
  MoreHorizontal,
  ExternalLink,
  Trash2,
  CalendarClock,
  SlidersHorizontal,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  FIELD_CLS,
  PRIMARY_BTN,
  DIALOG_CONTENT_CLS,
  ICON_TILES,
  SHADOWS,
  InfoTooltip,
  UserAvatar,
  DatePicker,
  BadgeNew,
} from "../components/designSystem";
import { formatCurrency, formatNumber } from "../utils/formatters";
import { BadgeArretrati, BadgeDaRinnovare } from "../components/PagamentiArretrati";
import {
  ModaleRegistraPagamenti, apriRegistraPagamenti, precaricaPagamenti, canoneServizi,
  puoImpostareScadenza, prossimaScadenza, salvaFatturaEmessa,
} from "../components/PagamentiCliente";
import {
  ModaleGirati,
  ModaleCallObiettivo,
  ModaleGiratoRecupero,
  ModaleTask,
  ModaleFollower,
  ModaleServizi,
  ModaleDatiAmministrativi,
} from "../components/ModaliClienti";
import { toast } from "sonner";
import { apiFetch, API_BASE as API } from "../utils/api";
import useStatoSessione from "../hooks/useStatoSessione";

// All available task types with labels
const ALL_TASK_TYPES = [
  { id: "script_cliente", label: "Script al Cliente" },
  { id: "ped_programmazione", label: "PED + Programmazione" },
  { id: "avvio_adv", label: "Avvio Sponsorizzate / ADV" },
  { id: "appuntamento_successivo", label: "Appuntamento Mese Successivo" },
  { id: "report_fine_mese", label: "Report di Fine Mese" },
  { id: "editing_video", label: "Editing Video" },
];

// Reparti disponibili
const REPARTI = [
  { id: "tiktok_shop", label: "TikTok Shop" },
  { id: "gestione_meta_tiktok", label: "Gestione Meta e TikTok" },
];

// Servizi disponibili per ogni reparto
const SERVIZI_PER_REPARTO = {
  tiktok_shop: [
    { id: "ts_tiktok_shop", label: "TikTok Shop" },
    { id: "ts_campagne", label: "Campagne" },
    { id: "ts_affiliazione", label: "Affiliazione" },
    { id: "ts_live", label: "Live" },
    { id: "ts_shooting_foto", label: "Shooting Fotografico" },
  ],
  gestione_meta_tiktok: [
    { id: "gmt_social_organico_8", label: "Social Organico (8 video)" },
    { id: "gmt_social_organico_12", label: "Social Organico (12 video)" },
    { id: "gmt_meta_ads", label: "Meta Ads" },
    { id: "gmt_google_ads", label: "Google Ads" },
    { id: "gmt_gestione_sito", label: "Gestione Sito" },
    { id: "gmt_tiktok_ads", label: "TikTok Ads" },
    { id: "gmt_shooting_foto", label: "Shooting Fotografico" },
    { id: "gmt_crm", label: "CRM" },
  ]
};

// Labels brevi per i servizi
const SERVIZI_LABELS = {
  ts_tiktok_shop: "TikTok Shop",
  ts_campagne: "Campagne",
  ts_affiliazione: "Affiliazione",
  ts_live: "Live",
  ts_shooting_foto: "Shooting",
  gmt_social_organico_8: "Social 8v",
  gmt_social_organico_12: "Social 12v",
  gmt_meta_ads: "Meta Ads",
  gmt_google_ads: "Google Ads",
  gmt_gestione_sito: "Sito",
  gmt_tiktok_ads: "TikTok Ads",
  gmt_shooting_foto: "Shooting",
  gmt_crm: "CRM"
};

const REPARTO_LABELS = {
  tiktok_shop: "TikTok Shop",
  gestione_meta_tiktok: "Gestione Meta/TikTok"
};

// Legacy services (for backwards compatibility)
const ALL_SERVIZI = [
  { id: "tiktok_shop", label: "Gestione TikTok Shop" },
  { id: "social_adv_8", label: "Gestione Social ADV e Organico (8 video/mese)" },
  { id: "social_adv_12", label: "Gestione Social ADV e Organico (12 video/mese)" },
  { id: "sito", label: "Sito Web" },
  { id: "google_ads", label: "Google Ads" },
  { id: "crm", label: "CRM" },
];

// ==================== Lista clienti: elementi della tabella ====================
// Redesign "tabella compatta" (design_handoff_clienti, opzione 8a): 7 colonne al
// posto di 17. Le funzioni sono le stesse di prima, raggruppate: team in un
// popover, date in una colonna, azioni rare nel menu ⋯. Nessun parallasse: al
// suo posto animazioni brevi di conferma sulle azioni (classi clienti-* in index.css).

const fmtEuroIt = (v) => formatCurrency(v, { decimals: 0 });

// Dovuto del mese se c'è, altrimenti il canone pattuito (es. mensili che
// partono dal mese dopo l'attivazione): la colonna non resta vuota.
const importoDelMese = (client, canone) => client.stato_pagamenti?.mese_corrente?.dovuto || canone.fisso;

// Quota sul fatturato generato da aggiungere al dovuto del mese. Con un
// servizio del Catalogo a percentuale il dovuto la contiene già, quando il
// fatturato del mese prima è inserito (quota_percentuale, stato_pagamenti.py):
// si somma solo quella del vecchio tag TikTok Shop, che il dovuto non conosce.
const quotaDaSommare = (client, tk) => (client.stato_pagamenti?.mese_corrente?.quota_percentuale ? 0 : tk?.tiktok_agency_fee || 0);

// Canone del mese saltato dall'amministrazione (stato_pagamenti.py,
// mese_corrente.saltato): {by_name, at, motivo} o null. Il backend lo dà per
// saldato, ma nella lista va distinto da un mese pagato davvero.
const saltatoDelMese = (client) => client.stato_pagamenti?.mese_corrente?.saltato || null;
const descrizioneSalto = (s) => `Saltato da ${s.by_name || "amministrazione"} il ${new Date(s.at).toLocaleDateString("it-IT")}: ${s.motivo}`;
// Grigio neutro, lo stesso dello stato "saltato" nella sezione Pagamenti della scheda.
const STILE_SALTATO = { background: "#f1f1f6", color: "#6b7280" };

// Creato una volta sola: costruire un Collator a ogni render della lista costa.
const COLLATOR_NOMI = new Intl.Collator("it", { sensitivity: "base", numeric: true });

// Righe disegnate subito al primo caricamento: bastano a riempire lo schermo.
// Le altre (sono ~270 attivi, ognuna con tooltip e team) arrivano nel render
// successivo, a bassa priorità, così la pagina compare senza aspettarle.
const RIGHE_PRIMO_RENDER = 30;

function dataBreve(d) {
  if (!d) return null;
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return null;
  return `${String(x.getDate()).padStart(2, "0")}/${String(x.getMonth() + 1).padStart(2, "0")}/${String(x.getFullYear()).slice(2)}`;
}

// Toast di conferma: bianco, livello 3, spunta verde. Sostituisce il toast verde
// pieno di richColors sulle azioni della lista, che sono frequenti e piccole.
function toastConferma(testo) {
  toast.custom(() => (
    <div
      className="flex items-center gap-2.5 pl-3 pr-4 py-2.5 rounded-[14px] bg-white border border-[#e9e9f1] scheda-font-ui"
      style={{ boxShadow: SHADOWS.flottante }}
    >
      <span className="w-[22px] h-[22px] rounded-full bg-[#e7f7ee] text-[#0f7040] flex items-center justify-center shrink-0">
        <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
      </span>
      <span className="text-[13px] font-semibold text-[#15161c]">{testo}</span>
    </div>
  ), { duration: 2400 });
}

// Colonna Fatturazione, per l'amministrazione: la scadenza dei pagamenti in sola
// lettura (si imposta dalla scheda, sezione Pagamenti) e il segno "fattura del
// mese emessa". Toccare la pillola apre il calendario: si sceglie la data di
// emissione, si corregge o si cancella. Cancellare offre "Annulla", perché un
// tocco sbagliato su una riga perderebbe una data reale.
function ColonnaFatturazione({ client, canEdit, onAggiorna, testId }) {
  const [saving, setSaving] = useState(false);
  const scadenza = client.data_scadenza_pagamenti;
  const prossima = prossimaScadenza(scadenza);
  const fattura = client.fattura_mese;
  // Mese saltato e nessuna fattura: non c'è niente da fatturare.
  const saltato = !fattura && saltatoDelMese(client);

  const imposta = async (data) => {
    if ((data || "") === (fattura?.data || "")) return;
    const precedente = fattura?.data;
    setSaving(true);
    try {
      const salvata = await salvaFatturaEmessa(client.client_id, data);
      if (salvata === undefined) return;
      onAggiorna(salvata);
      if (data) {
        toastConferma(`Fattura emessa il ${data.split("-").reverse().join("/")}`);
      } else {
        // L'annullamento non passa da imposta: la sua closure vedrebbe ancora
        // la vecchia fattura e scarterebbe il ripristino come "nessun cambio".
        toast(`Fattura di ${client.name} segnata da emettere`, {
          action: {
            label: "Annulla",
            onClick: async () => {
              const ripristinata = await salvaFatturaEmessa(client.client_id, precedente);
              if (ripristinata !== undefined) onAggiorna(ripristinata);
            },
          },
          duration: 5000,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const pill = (toggle) => (
    <button
      type="button"
      disabled={!canEdit || saving}
      onClick={toggle}
      className="h-[22px] px-2 rounded-full text-[11.5px] font-semibold inline-flex items-center gap-1.5 transition-[filter,transform] duration-200 enabled:cursor-pointer enabled:hover:brightness-95 enabled:active:scale-95 disabled:cursor-default motion-reduce:transition-none"
      style={fattura ? { background: "#e7f7ee", color: "#0f7040" } : saltato ? STILE_SALTATO : { background: "#fdf3e1", color: "#8a6414" }}
      data-testid={`${testId}-fattura`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: fattura ? "#10b981" : saltato ? "#9095a1" : "#f59e0b" }} />
      {fattura ? "Fatturato" : saltato ? "Non si fattura" : "Da fatturare"}
      {fattura && <span className="scheda-font-mono font-medium">{dataBreve(fattura.data)?.slice(0, 5)}</span>}
    </button>
  );

  return (
    <div className="flex flex-row flex-wrap items-center gap-x-3 gap-y-1.5 md:flex-col md:items-start text-[12.5px]" data-testid={testId}>
      {scadenza ? (
        <InfoTooltip
          title="Scadenza pagamenti"
          description={`Il cliente paga entro il ${Number(scadenza.slice(8, 10))} di ogni mese. Prossima scadenza: ${prossima.toLocaleDateString("it-IT")}. Si modifica dalla scheda cliente, sezione Pagamenti.`}
        >
          <span className="flex items-center gap-1.5" data-testid={`${testId}-scadenza`}>
            <CalendarClock className="w-[13px] h-[13px] text-[#9095a1] shrink-0" />
            <span className="text-[#9095a1]">Scade</span>
            <span className="scheda-font-mono text-[#15161c]">{dataBreve(prossima)?.slice(0, 5)}</span>
          </span>
        </InfoTooltip>
      ) : (
        <span className="flex items-center gap-1.5" data-testid={`${testId}-scadenza`}>
          <CalendarClock className="w-[13px] h-[13px] text-[#9095a1] shrink-0" />
          <span className="text-[#9095a1]">Scade</span>
          <span className="text-[#b4b8c5]">—</span>
        </span>
      )}
      <InfoTooltip
        title={fattura ? "Fattura del mese emessa" : saltato ? "Mensile del mese saltato" : "Fattura del mese da emettere"}
        description={fattura
          ? `Emessa il ${fattura.data.split("-").reverse().join("/")}.${canEdit ? " Tocca per cambiare la data o cancellarla." : ""}`
          : saltato
            ? `${descrizioneSalto(saltato)}. Il canone di questo mese non si fattura.`
            : `Nessuna fattura segnata per questo mese.${canEdit ? " Tocca per scegliere la data di emissione." : ""}`}
      >
        {canEdit ? (
          <span className="inline-flex">
            <DatePicker
              value={fattura?.data || ""}
              onChange={imposta}
              renderTrigger={({ toggle }) => pill(toggle)}
            />
          </span>
        ) : pill()}
      </InfoTooltip>
    </div>
  );
}

// Ruoli del team nella colonna Team, nell'ordine degli avatar. `field` è il campo
// che handleAssignRepartoStaff scrive; `valore` legge l'assegnazione attuale
// (lo SMM può essere in tre campi diversi per storia del dato).
const RUOLI_TEAM = [
  { id: "pm", label: "PM", nome: "Project Manager", iniziale: "P", field: "assigned_pm_id", tone: ICON_TILES.viola, valore: (c) => c.assigned_pm_id },
  { id: "smm", label: "SMM", nome: "SMM", iniziale: "S", field: "smm_tiktok_id", tone: ICON_TILES.teal, valore: (c) => c.assigned_smm_id || c.smm_tiktok_id || c.smm_social_id },
  { id: "cc", label: "CC", nome: "Content Creator", iniziale: "C", field: "videomaker_id", tone: ICON_TILES.rosa, valore: (c) => c.videomaker_id },
  { id: "editor", label: "Editor", nome: "Editor", iniziale: "E", field: "editor_id", tone: ICON_TILES.arancio, valore: (c) => c.editor_id },
];

// Scadenza del contratto in 12 segmenti, letta come una batteria: piena a inizio
// contratto, si svuota man mano che la scadenza si avvicina e a contratto scaduto
// è vuota (lì subentra il badge "Da rinnovare"). Sostituisce la vecchia ContrattoScadenzaBar. contratto_data_scadenza arriva dal
// contratto collegato per i clienti nati dal wizard (PATCH /contratti/{id}/durata)
// e, per i clienti precedenti, dalla scadenza dichiarata nella scheda cliente
// (backend/routes/contratto_pregresso.py). Senza nessuna delle due: niente riga.
// con le date agli estremi e i giorni residui. Larghezza fissa, così le righe si
// allineano. Solo lettura: niente cursore, il dettaglio è nel tooltip.
function ScadenzaSegmenti({ client }) {
  if (!client.contratto_data_scadenza) return null;
  const fine = new Date(client.contratto_data_scadenza);
  const inizio = client.contratto_data_inizio ? new Date(client.contratto_data_inizio) : null;
  const adesso = new Date();
  const giorni = Math.ceil((fine - adesso) / 86400000);
  const durata = inizio ? fine - inizio : null;
  const residuo = durata && durata > 0 ? Math.min(1, Math.max(0, (fine - adesso) / durata)) : (giorni < 0 ? 0 : 1);
  // Arrotondato per eccesso: finché manca anche un solo giorno resta almeno un segmento acceso.
  const pieni = giorni < 0 ? 0 : Math.ceil(12 * residuo);
  const tono = giorni < 7 ? { seg: "#e05a4a", testo: "#c0392b" } : giorni < 30 ? { seg: "#f59e0b", testo: "#b45309" } : { seg: "#8f8fe6", testo: "#565d6d" };
  const descrizione = giorni < 0
    ? `Il contratto è scaduto da ${Math.abs(giorni)} giorni`
    : giorni === 0 ? "Il contratto scade oggi" : `Mancano ${giorni} giorni alla scadenza del contratto`;
  return (
    <InfoTooltip title="Scadenza contratto" description={descrizione}>
      <div className="flex items-center gap-2 w-[340px] max-w-full mt-1.5">
        <span className="scheda-font-mono text-[10.5px] text-[#9095a1] shrink-0">{inizio ? dataBreve(inizio) : "—"}</span>
        <div className="flex-1 flex gap-[2px] pointer-events-none" aria-hidden="true">
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} className="flex-1 h-[5px] rounded-[1.5px]" style={{ background: i < pieni ? tono.seg : "#ececf3" }} />
          ))}
        </div>
        <span className="scheda-font-mono text-[10.5px] text-[#9095a1] shrink-0">{dataBreve(fine)}</span>
        <span className="scheda-font-mono text-[11.5px] font-semibold w-12 text-right shrink-0" style={{ color: tono.testo }}>
          {giorni < 0 ? "scad." : `${giorni} gg`}
        </span>
      </div>
    </InfoTooltip>
  );
}

// Casella di selezione 18px: tre stati (vuota, piena, parziale "–").
function CasellaSelezione({ stato, onClick, testId, label }) {
  const pieno = stato === "tutti" || stato === true;
  const parziale = stato === "parziale";
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={parziale ? "mixed" : pieno}
      aria-label={label}
      onClick={onClick}
      className="w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center transition-[background-color,border-color,transform] duration-150 active:scale-90 motion-reduce:transition-none"
      style={pieno || parziale ? { background: "#5b5bd6", borderColor: "#5b5bd6" } : { background: "#fff", borderColor: "#c9ccd6" }}
      data-testid={testId}
    >
      {pieno && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
      {parziale && <span className="w-2 h-0.5 rounded bg-white" />}
    </button>
  );
}

// Select di una persona dentro il popover Team: avatar 24px nel campo e nell'elenco.
function SelectPersona({ users, value, onChange, testId }) {
  return (
    <Select value={value || "unassigned"} onValueChange={onChange}>
      <SelectTrigger
        className={`${FIELD_CLS} w-full h-[34px] pl-1 text-[13px] shadow-[inset_0_1px_2px_rgba(15,23,42,.04)] hover:border-[#c9c9f0] [&>span]:!flex [&>span]:items-center [&>span]:min-w-0`}
        data-testid={testId}
      >
        <SelectValue placeholder="Non assegnato" />
      </SelectTrigger>
      <SelectContent className="scheda-font-ui">
        <SelectItem value="unassigned">
          <span className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full border-[1.5px] border-dashed border-[#d7d9e2] shrink-0" aria-hidden="true" />
            <span className="text-[#9095a1]">Non assegnato</span>
          </span>
        </SelectItem>
        {users.map((u) => (
          <SelectItem key={u.user_id} value={u.user_id}>
            <span className="flex items-center gap-2 min-w-0">
              <UserAvatar name={u.name} picture={u.picture} size={24} />
              <span className="truncate">{u.name}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Team a cerchio: i 4 ruoli disposti in senso orario attorno a un anello (PM in
// alto, SMM a destra, CC in basso, Editor a sinistra), così stanno in un
// quadrato di 56px invece che in una fila. I ruoli scoperti sono cerchi
// tratteggiati con l'iniziale. Al clic un popover con le 4 select; ogni cambio
// si salva subito.
const POSIZIONI_TEAM = [
  "top-0 left-1/2 -translate-x-1/2",
  "right-0 top-1/2 -translate-y-1/2",
  "bottom-0 left-1/2 -translate-x-1/2",
  "left-0 top-1/2 -translate-y-1/2",
];

function TeamPill({ client, liste, onAssegna, versoAlto, bump, testId }) {
  const [aperto, setAperto] = useState(false);
  const persone = RUOLI_TEAM.map((r) => {
    const uid = r.valore(client);
    return { r, uid, user: uid ? liste[r.id].find((u) => u.user_id === uid) : null };
  });
  const assegnati = persone.filter((p) => p.uid).length;

  return (
    <Popover open={aperto} onOpenChange={setAperto}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative block w-14 h-14 rounded-full hover:bg-[#f3f3fa] transition-colors"
          aria-label={`Team di ${client.name}: ${assegnati} ruoli su 4 assegnati`}
          data-testid={testId}
        >
          {/* Anello che passa per il centro dei 4 avatar. */}
          <span className="absolute inset-[11px] rounded-full border border-[#e9e9f1]" aria-hidden="true" />
          {persone.map(({ r, uid, user }, i) => (
            <span key={r.id} className={`absolute flex ${POSIZIONI_TEAM[i]}`}>
              <InfoTooltip title={r.nome} description={uid ? (user?.name || "Utente non più attivo") : "Non assegnato"}>
                <span className={`inline-flex rounded-full ${bump === r.id ? "clienti-bump" : ""}`}>
                  {uid ? (
                    <span className="inline-flex rounded-full border-2 border-white" style={{ boxShadow: SHADOWS.appoggiato }}>
                      <UserAvatar name={user?.name || r.iniziale} picture={user?.picture} size={18} tone={r.tone} />
                    </span>
                  ) : (
                    <span className="w-[22px] h-[22px] rounded-full border-[1.5px] border-dashed border-[#d7d9e2] flex items-center justify-center text-[9px] font-bold text-[#b4b8c5] bg-white">
                      {r.iniziale}
                    </span>
                  )}
                </span>
              </InfoTooltip>
            </span>
          ))}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={versoAlto ? "top" : "bottom"}
        align="start"
        className={`w-[300px] p-3 rounded-[16px] border-[#e9e9f1] scheda-font-ui ${versoAlto ? "clienti-pop-su" : "clienti-pop"}`}
        style={{ boxShadow: SHADOWS.flottante }}
      >
        <div className="flex flex-col gap-2.5">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-[#9095a1] truncate">Team di {client.name}</span>
          {persone.map(({ r, uid }) => (
            <div key={r.id} className="grid grid-cols-[78px_minmax(0,1fr)] gap-2 items-center">
              <span className="text-[12.5px] font-semibold text-[#3c414d]">{r.label}</span>
              <SelectPersona
                users={liste[r.id]}
                value={uid}
                onChange={(v) => onAssegna(r, v)}
                testId={`team-${r.id}-select-${client.client_id}`}
              />
            </div>
          ))}
          <button type="button" onClick={() => setAperto(false)} className={`${PRIMARY_BTN} h-8 mt-1`}>
            Fatto
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Barra di selezione multipla in basso al centro, al posto del blocco sopra la
// tabella. In un portal per restare fissa sulla finestra.
function BarraSelezione({ n, smmUsers, onAssegna, onDeseleziona }) {
  const visibile = n > 0;
  return createPortal(
    <div
      className="fixed left-1/2 lg:left-[calc(50%+8rem)] bottom-24 lg:bottom-7 z-40 flex items-center gap-3 pl-[18px] pr-2 py-2 rounded-[16px] bg-[rgba(21,22,28,.92)] backdrop-blur-[12px] whitespace-nowrap scheda-font-ui motion-reduce:transition-none"
      style={{
        boxShadow: SHADOWS.flottante,
        opacity: visibile ? 1 : 0,
        transform: `translate(-50%, ${visibile ? 0 : 24}px)`,
        pointerEvents: visibile ? "auto" : "none",
        transition: "opacity .3s, transform .4s cubic-bezier(.22,.7,.3,1)",
      }}
      aria-hidden={!visibile}
    >
      <span className="text-[13px] font-semibold text-white">
        {n} {n === 1 ? "cliente selezionato" : "clienti selezionati"}
      </span>
      <Select value="" onValueChange={(v) => onAssegna(v === "unassigned" ? null : v)}>
        <SelectTrigger
          className="h-9 w-auto min-w-[160px] rounded-[10px] border-0 bg-white/10 text-white text-[13px] font-semibold focus:ring-0 [&>svg]:text-white"
          data-testid="bulk-assign-select"
        >
          <SelectValue placeholder="Assegna SMM…" />
        </SelectTrigger>
        <SelectContent className="scheda-font-ui">
          <SelectItem value="unassigned">Nessuno</SelectItem>
          {smmUsers.map((u) => (
            <SelectItem key={u.user_id} value={u.user_id}>
              <span className="flex items-center gap-2">
                <UserAvatar name={u.name} picture={u.picture} size={22} />
                {u.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        type="button"
        onClick={onDeseleziona}
        className="h-9 px-3.5 rounded-[10px] text-[#d7d9e2] text-[13px] font-semibold hover:bg-white/[.08] transition-colors"
      >
        Deseleziona
      </button>
    </div>,
    document.body
  );
}

// Select filtro 138px della barra filtri: quando filtra qualcosa si colora.
function FiltroSelect({ value, onChange, attivo, testId, children, placeholder }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className={`${FIELD_CLS} w-full md:w-[138px] h-[38px] text-[13px] [&>span]:truncate ${attivo ? "border-[#c9c9f0] bg-[#f7f7fe] text-[#5b5bd6] font-semibold" : "shadow-[inset_0_1px_2px_rgba(15,23,42,.04)]"}`}
        data-testid={testId}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="scheda-font-ui">{children}</SelectContent>
    </Select>
  );
}

// Segnaposto della pagina durante il primo caricamento: stessa intestazione,
// stessa barra filtri e stessa griglia della tabella (colonne della vista
// completa), così all'arrivo dei dati niente salta di posto.
const COLONNE_SKELETON = "44px minmax(240px,1fr) 170px 68px 120px 118px 152px";

function SkeletonClienti() {
  const barra = "bg-[#f3f3f8]";
  return (
    <div className="flex flex-col gap-[18px] scheda-font-ui" data-testid="clients-loading" aria-busy="true" aria-label="Caricamento clienti">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-[30px] font-bold tracking-[-.02em] text-[#15161c]">Clienti</h1>
          <Skeleton className="bg-[#e9e9f1] h-[14px] w-[120px] mt-1.5 rounded-full" />
        </div>
        {/* Fuori dalle card il fondo è sfumato: il tono più chiaro sparirebbe. */}
        <div className="flex gap-2">
          <Skeleton className="bg-[#e9e9f1] h-10 w-[136px] rounded-[12px]" />
          <Skeleton className="bg-[#e9e9f1] h-10 w-[140px] rounded-[12px]" />
        </div>
      </div>

      <div
        className="flex items-center gap-2 flex-wrap p-2.5 rounded-[18px] bg-white/[.82] border border-[rgba(233,233,241,.9)]"
        style={{ boxShadow: SHADOWS.card }}
      >
        <Skeleton className={`${barra} h-[38px] w-[150px] rounded-[12px]`} />
        <Skeleton className={`${barra} h-[38px] flex-1 min-w-[160px] rounded-[10px]`} />
        {[118, 96, 96, 96, 104].map((w, i) => (
          <Skeleton key={i} className={`${barra} h-[38px] rounded-[10px]`} style={{ width: w }} />
        ))}
      </div>

      <div className="bg-white rounded-[18px] border border-[#e9e9f1] overflow-hidden" style={{ boxShadow: SHADOWS.card }}>
        <div className="overflow-x-auto">
          <div className="min-w-[1008px]">
            <div className="grid gap-3 px-[18px] items-center py-3 bg-[#fbfbfd] text-[11px] font-semibold uppercase tracking-[.06em] text-[#9095a1]" style={{ gridTemplateColumns: COLONNE_SKELETON }}>
              <span />
              <span>Cliente</span>
              <span>Date</span>
              <span>Team</span>
              <span className="text-right">€ Mensile</span>
              <span>Stato</span>
              <span className="text-right">Azioni</span>
            </div>
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="grid gap-3 px-[18px] items-center py-3.5 border-t border-[#f0f0f6]"
                style={{ gridTemplateColumns: COLONNE_SKELETON }}
              >
                <Skeleton className={`${barra} w-[18px] h-[18px] rounded-[6px]`} />
                <div className="flex flex-col gap-1.5">
                  {/* Larghezze diverse riga per riga: una colonna tutta uguale sembra una griglia vuota. */}
                  <Skeleton className={`${barra} h-[15px] rounded-full`} style={{ width: `${55 - (i % 3) * 12}%` }} />
                  <Skeleton className={`${barra} h-[22px] w-[92px] rounded-[7px]`} />
                </div>
                <div className="flex flex-col gap-[7px]">
                  <Skeleton className={`${barra} h-[11px] w-[110px] rounded-full`} />
                  <Skeleton className={`${barra} h-[11px] w-[88px] rounded-full`} />
                  <Skeleton className={`${barra} h-[11px] w-[100px] rounded-full`} />
                </div>
                <Skeleton className={`${barra} w-14 h-14 rounded-full`} />
                <div className="flex flex-col items-end gap-1.5">
                  <Skeleton className={`${barra} h-[15px] w-[64px] rounded-full`} />
                  <Skeleton className={`${barra} h-[22px] w-[70px] rounded-full`} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Skeleton className={`${barra} h-[26px] w-[74px] rounded-full`} />
                  <Skeleton className={`${barra} h-[11px] w-[56px] rounded-full`} />
                </div>
                <div className="flex justify-end gap-1">
                  {[0, 1, 2].map((k) => <Skeleton key={k} className={`${barra} w-8 h-8 rounded-[9px]`} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClientsPage({ user }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [smmUsers, setSmmUsers] = useState([]);
  const [pmUsers, setPmUsers] = useState([]);
  const [videomakeUsers, setVideomakeUsers] = useState([]);
  const [editorUsers, setEditorUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [righeLimite, setRigheLimite] = useState(RIGHE_PRIMO_RENDER);
  const [searchQuery, setSearchQuery] = useStatoSessione("clienti:ricerca", "");
  // Il filtro legge la ricerca differita: il campo resta reattivo mentre la
  // lista si ridisegna.
  const ricercaDifferita = useDeferredValue(searchQuery);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  // Wizard mappatura import
  const [importStep, setImportStep] = useState("upload"); // upload | mapping | review | done
  const [importAnalysis, setImportAnalysis] = useState(null);
  const [importMapping, setImportMapping] = useState({});
  const [importPreview, setImportPreview] = useState(null); // anteprima delle modifiche
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  // Cliente di cui è aperta la modale Girati (ModaliClienti.jsx).
  const [giratiClient, setGiratiClient] = useState(null);
  const [tiktokFees, setTiktokFees] = useState({}); // client_id → { monthly_total_agency, tiktok_agency_fee, ... }
  
  // Filtri per PM, SMM, Content Creator (videomaker), Editor. Restano salvati
  // nella sessione: tornando da una scheda cliente la lista è come la si era lasciata.
  const [filterPM, setFilterPM] = useStatoSessione("clienti:filtroPM", "all");
  const [filterSMM, setFilterSMM] = useStatoSessione("clienti:filtroSMM", "all");
  const [filterCC, setFilterCC] = useStatoSessione("clienti:filtroCC", "all");
  const [filterEditor, setFilterEditor] = useStatoSessione("clienti:filtroEditor", "all");
  // Filtri sullo stato del cliente, oltre a quelli sul team.
  const [filterPagato, setFilterPagato] = useStatoSessione("clienti:filtroPagato", "all");      // all | pagato | non_pagato
  const [filterServizio, setFilterServizio] = useStatoSessione("clienti:filtroServizio", "all");  // all | nessuno | servizio_id del Catalogo
  const [filterContratto, setFilterContratto] = useStatoSessione("clienti:filtroContratto", "all"); // all | in_scadenza | scaduto | senza
  const [filterTeam, setFilterTeam] = useStatoSessione("clienti:filtroTeam", "all");          // all | completo | incompleto
  const [filterMensile, setFilterMensile] = useStatoSessione("clienti:filtroMensile", "all");  // all | percentuale | fisso
  // Direzione dell'ultimo cambio tab Attivi/Inattivi, per l'animazione della tabella.
  const [direzioneVista, setDirezioneVista] = useState(1);
  
  // Cartella clienti inattivi
  // Tab Attivi / Inattivi della barra filtri (prima: cartella "Clienti inattivi" in fondo).
  const [vistaClienti, setVistaClienti] = useStatoSessione("clienti:vista", "attivi");
  // Solo su mobile: i filtri stanno dietro un pulsante, altrimenti la barra
  // sticky occupa metà dello schermo e copre la lista mentre si scorre.
  const [filtriMobileAperti, setFiltriMobileAperti] = useState(false);
  const tabRefs = useRef({});
  const [indicatoreTab, setIndicatoreTab] = useState({ left: 3, width: 0 });
  // Conferme visive dopo una risposta positiva dell'API: la riga lampeggia, e
  // nel team l'avatar cambiato fa un piccolo rimbalzo.
  const [flashRiga, setFlashRiga] = useState(null);
  const [bumpTeam, setBumpTeam] = useState(null);
  // Ordinamento clienti: 'name_asc' (default A-Z), 'name_desc' (Z-A), 'data_girato_asc', 'data_girato_desc', 'importo_desc'
  const [sortBy, setSortBy] = useStatoSessione("clienti:ordinamento", "name_asc");
  
  // New client dialog state
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const [newClientData, setNewClientData] = useState({
    name: "",
    data_girato: "",
    obiettivo_organico: "",
    percentuale_crescita: "",
    obiettivo_adv: "",
    assigned_smm_id: "",
    assigned_pm_id: "",
    importo_mensile: "",
    enabled_tasks: ALL_TASK_TYPES.map(t => t.id)
  });
  const [savingClient, setSavingClient] = useState(false);
  
  // Edit tasks dialog state
  const [editingTasksClient, setEditingTasksClient] = useState(null);
  const [editingEnabledTasks, setEditingEnabledTasks] = useState([]);
  const [savingTasks, setSavingTasks] = useState(false);
  
  // Admin edit dialog state
  const [editingAdminClient, setEditingAdminClient] = useState(null);
  const [adminData, setAdminData] = useState({ name: "", importo_mensile: "", contratto_url: "", ha_pagato: true });
  const [savingAdmin, setSavingAdmin] = useState(false);
  
  // Call obiettivo dialog state
  const [editingCallClient, setEditingCallClient] = useState(null);
  const [callObiettivoDate, setCallObiettivoDate] = useState("");
  const [savingCall, setSavingCall] = useState(false);
  
  // Girato recupero dialog state
  const [editingRecuperoClient, setEditingRecuperoClient] = useState(null);
  const [recuperoData, setRecuperoData] = useState({ has_girato_recupero: false, data_girato_recupero: "" });
  const [savingRecupero, setSavingRecupero] = useState(false);
  
  // Follower stats dialog state
  const [editingFollowerClient, setEditingFollowerClient] = useState(null);
  const [followerData, setFollowerData] = useState({ instagram: "", tiktok: "", youtube: "", facebook: "" });
  // Storico follower del cliente aperto: dà il mese precedente per la crescita.
  const [followerStorico, setFollowerStorico] = useState([]);
  const [savingFollower, setSavingFollower] = useState(false);
  
  // Servizi dialog state - Nuova struttura per reparto con importi
  const [editingServiziClient, setEditingServiziClient] = useState(null);
  const [serviziRepartoData, setServiziRepartoData] = useState({});  // {reparto_id: {servizi: [], importo: 0}}
  const [savingServizi, setSavingServizi] = useState(false);
  
  // Delete client dialog state
  const [deletingClient, setDeletingClient] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Il pulsante Attivo/Inattivo cambiava stato al primo clic: in una lista
  // fitta bastava un clic sbagliato per togliere un cliente da task e scadenze.
  const [clienteDaCambiareStato, setClienteDaCambiareStato] = useState(null);
  const [cambioStatoSaving, setCambioStatoSaving] = useState(false);
  
  const fileInputRef = useRef(null);

  const isDirettore = user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale" || !!user?.is_admin;
  const isPM = user?.role === "pm" || isDirettore || (user?.secondary_roles || []).includes("pm") || !!user?.is_admin;
  const isAMM = user?.role === "amm" || isDirettore || (user?.secondary_roles || []).includes("amm") || !!user?.is_admin;
  const isSMM = user?.role === "smm" || user?.role === "videomaker";
  // Accesso totale (vede e modifica tutto come admin): admin email, Resp.Operativo, Direttore Tecnico/Commerciale, AMM
  const isAdmin = user?.email === "info@diecixcento.com" || user?.is_responsabile_operativo || isDirettore || isAMM;
  const canEditAdmin = isPM || isAMM;
  // Attivo/Inattivo: anche l'amministrazione, che segue chiusure e riprese dei contratti
  const canCambiareStato = isPM || isAMM;
  // Mostra colonna Call Obiettivo per admin / resp.operativo / PM / SMM
  const canViewCallColumn = isAdmin || user?.is_responsabile_operativo || isPM || user?.role === "smm";

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (loading || righeLimite === Infinity) return;
    const t = setTimeout(() => startTransition(() => setRigheLimite(Infinity)), 0);
    return () => clearTimeout(t);
  }, [loading, righeLimite]);

  // Posizione dell'indicatore dei tab: si misura il tab scelto, perché i due tab
  // hanno larghezze diverse (il contatore cambia con i filtri).
  useLayoutEffect(() => {
    const el = tabRefs.current[vistaClienti];
    if (!el) return;
    const left = el.offsetLeft;
    const width = el.offsetWidth;
    // Solo se cambia: l'effetto gira a ogni render, un oggetto nuovo sempre
    // diverso farebbe ridisegnare la pagina all'infinito.
    setIndicatoreTab((p) => (p.left === left && p.width === width ? p : { left, width }));
  });

  const fetchData = async () => {
    try {
      const [clientsRes, smmRes, pmRes, vmRes, editorRes, tiktokRes] = await Promise.all([
        apiFetch("/clients"),
        apiFetch("/users/smm"),
        apiFetch("/users/pm"),
        apiFetch("/users/videomaker"),
        apiFetch("/users/editor"),
        apiFetch("/tiktok-fees/all"),
      ]);

      if (clientsRes.ok) setClients(await clientsRes.json());
      if (smmRes.ok) setSmmUsers(await smmRes.json());
      if (pmRes.ok) setPmUsers(await pmRes.json());
      if (vmRes.ok) setVideomakeUsers(await vmRes.json());
      if (editorRes.ok) setEditorUsers(await editorRes.json());
      if (tiktokRes.ok) {
        const d = await tiktokRes.json();
        setTiktokFees(d.clients || {});
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const token = localStorage.getItem("session_token");
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    setImportLoading(true);
    const loadingToast = toast.loading(`Sto analizzando ${file.name}...`);

    try {
      const response = await fetch(`${API}/import/excel/analyze`, {
        method: "POST",
        headers,
        body: formData,
      });
      const result = await response.json();
      toast.dismiss(loadingToast);

      if (response.ok) {
        setImportAnalysis(result);
        setImportMapping(result.suggested_mapping || {});
        setImportStep("mapping");
        toast.success(`File analizzato: ${result.total_rows} righe rilevate`);
      } else {
        toast.error(result.detail || "Errore durante l'analisi");
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error("Errore durante l'upload del file");
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSwitchSheet = async (newSheet, newHeaderRow = null) => {
    if (!importAnalysis?.session_id) return;
    setImportLoading(true);
    const token = localStorage.getItem("session_token");
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    try {
      const fd = new FormData();
      fd.append("session_id", importAnalysis.session_id);
      fd.append("sheet", newSheet || importAnalysis.current_sheet);
      if (newHeaderRow !== null && newHeaderRow !== undefined) {
        fd.append("header_row", String(newHeaderRow));
      }
      const res = await fetch(`${API}/import/excel/switch-sheet`, {
        method: "POST",
        headers,
        body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        setImportAnalysis(data);
        setImportMapping(data.suggested_mapping || {});
        if (newHeaderRow !== null) {
          toast.success(`Intestazioni lette dalla riga ${newHeaderRow + 1}`);
        } else {
          toast.success(`Foglio "${newSheet}" caricato (${data.total_rows} righe)`);
        }
      } else {
        toast.error(data.detail || "Errore cambio foglio");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    } finally {
      setImportLoading(false);
    }
  };

  const handlePreviewImport = async () => {
    if (!importAnalysis?.session_id) return;
    if (!importMapping.name) {
      toast.error("Devi mappare almeno il campo 'Nome Cliente'");
      return;
    }
    setImportLoading(true);
    const loadingToast = toast.loading("Sto preparando l'anteprima modifiche...");
    try {
      const response = await apiFetch("/import/excel/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: importAnalysis.session_id, mapping: importMapping }),
      });
      const result = await response.json();
      toast.dismiss(loadingToast);
      if (response.ok) {
        setImportPreview(result);
        setImportStep("review");
      } else {
        toast.error(result.detail || "Errore nell'anteprima");
      }
    } catch (e) {
      toast.dismiss(loadingToast);
      toast.error("Errore di connessione");
    } finally {
      setImportLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!importAnalysis?.session_id) return;
    if (!importMapping.name) {
      toast.error("Devi mappare almeno il campo 'Nome Cliente'");
      return;
    }

    setImportLoading(true);
    const loadingToast = toast.loading("Sto importando i clienti...");
    try {
      const response = await apiFetch("/import/excel/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: importAnalysis.session_id, mapping: importMapping }),
      });
      const result = await response.json();
      toast.dismiss(loadingToast);

      if (response.ok) {
        setImportResult(result);
        setImportStep("done");
        await fetchData();
        toast.success(
          `Import completato: ${result.imported || 0} nuovi · ${result.updated || 0} aggiornati`,
          { duration: 5000 }
        );
      } else {
        toast.error(result.detail || "Errore durante l'import");
      }
    } catch (e) {
      toast.dismiss(loadingToast);
      toast.error("Errore di connessione durante l'import");
    } finally {
      setImportLoading(false);
    }
  };

  const resetImportWizard = () => {
    setImportStep("upload");
    setImportAnalysis(null);
    setImportMapping({});
    setImportResult(null);
  };

  const handleAssignment = async (clientId, smmId) => {
    try {
      const response = await apiFetch(`/clients/${clientId}/assign?smm_id=${smmId || ""}`, {
        method: "PATCH"
      });

      if (response.ok) {
        toast.success("Cliente assegnato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nell'assegnazione");
    }
  };

  const handleBulkAssign = async (smmId) => {
    if (selectedClients.length === 0) {
      toast.error("Seleziona almeno un cliente");
      return;
    }

    try {
      const response = await apiFetch(`/clients/bulk-assign?smm_id=${smmId || ""}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_ids: selectedClients, smm_id: smmId })
      });

      if (response.ok) {
        const nome = smmUsers.find((u) => u.user_id === smmId)?.name;
        toastConferma(nome
          ? `SMM ${nome} assegnato a ${selectedClients.length} ${selectedClients.length === 1 ? "cliente" : "clienti"}`
          : `SMM rimosso da ${selectedClients.length} ${selectedClients.length === 1 ? "cliente" : "clienti"}`);
        setSelectedClients([]);
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nell'assegnazione di massa");
    }
  };


  // Handle new client creation
  const handleCreateClient = async () => {
    if (!newClientData.name.trim()) {
      toast.error("Il nome del cliente è obbligatorio");
      return;
    }

    setSavingClient(true);
    try {
      const payload = {
        name: newClientData.name.trim(),
        data_girato: newClientData.data_girato || null,
        obiettivo_organico: newClientData.obiettivo_organico || null,
        percentuale_crescita: newClientData.percentuale_crescita ? parseFloat(newClientData.percentuale_crescita) : null,
        obiettivo_adv: newClientData.obiettivo_adv || null,
        assigned_smm_id: newClientData.assigned_smm_id || null,
        assigned_pm_id: newClientData.assigned_pm_id || null,
        importo_mensile: newClientData.importo_mensile ? parseFloat(newClientData.importo_mensile) : null,
        enabled_tasks: newClientData.enabled_tasks
      };

      const response = await apiFetch("/clients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(`Cliente creato! ${result.tasks_generated} task generati`);
        setNewClientDialogOpen(false);
        setNewClientData({
          name: "",
          data_girato: "",
          obiettivo_organico: "",
          percentuale_crescita: "",
          obiettivo_adv: "",
          assigned_smm_id: "",
          assigned_pm_id: "",
          importo_mensile: "",
          enabled_tasks: ALL_TASK_TYPES.map(t => t.id)
        });
        fetchData();
      } else {
        toast.error(result.detail || "Errore nella creazione");
      }
    } catch (error) {
      toast.error("Errore nella creazione del cliente");
    } finally {
      setSavingClient(false);
    }
  };

  // Handle editing admin data for a client (PM and AMM only)
  const handleEditAdminData = (client) => {
    setEditingAdminClient(client);
    setAdminData({
      name: client.name || "",
      importo_mensile: client.importo_mensile || "",
      contratto_url: client.contratto_url || "",
      ha_pagato: client.ha_pagato !== false
    });
  };

  const handleSaveAdminData = async () => {
    if (!editingAdminClient) return;

    setSavingAdmin(true);
    try {
      const payload = {
        importo_mensile: adminData.importo_mensile ? parseFloat(adminData.importo_mensile) : null,
        contratto_url: adminData.contratto_url || null,
        ha_pagato: adminData.ha_pagato
      };
      // Includi il nome solo se admin (rinomina cliente)
      if (isAdmin && (adminData.name || "").trim() && adminData.name.trim() !== editingAdminClient.name) {
        payload.name = adminData.name.trim();
      }

      const response = await apiFetch(`/clients/${editingAdminClient.client_id}/admin`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toastConferma(`Dati amministrativi aggiornati · ${payload.name || editingAdminClient.name}`);
        lampeggia(editingAdminClient.client_id);
        setEditingAdminClient(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setSavingAdmin(false);
    }
  };

  // Handle assigning PM to client
  const handleAssignPM = async (clientId, pmId) => {
    try {
      const response = await apiFetch(`/clients/${clientId}/assign-pm?pm_id=${pmId || ""}`, {
        method: "PATCH"
      });
      if (response.ok) {
        toast.success("PM assegnato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nell'assegnazione");
    }
  };

  // Handle assigning PM/SMM per reparto
  const handleAssignRepartoStaff = async (clientId, field, value) => {
    try {
      const payload = {
        [field]: value === "unassigned" ? null : value
      };
      const response = await apiFetch(`/clients/${clientId}/assign-reparto-staff`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        fetchData();
        return true;
      }
      const error = await response.json();
      toast.error(error.detail || "Errore nell'assegnazione");
    } catch (error) {
      toast.error("Errore nell'assegnazione");
    }
    return false;
  };

  const lampeggia = (clientId) => {
    setFlashRiga(clientId);
    setTimeout(() => setFlashRiga((id) => (id === clientId ? null : id)), 900);
  };

  // Cambio di una persona dal popover Team: salva subito, poi conferma con
  // toast, flash della riga e rimbalzo dell'avatar (solo se l'API ha risposto ok).
  const assegnaTeam = async (client, ruolo, valore) => {
    const ok = await handleAssignRepartoStaff(client.client_id, ruolo.field, valore);
    if (!ok) return;
    const lista = { pm: pmUsers, smm: smmUsers, cc: videomakeUsers, editor: editorUsers }[ruolo.id];
    const nome = valore && valore !== "unassigned" ? lista.find((u) => u.user_id === valore)?.name : null;
    toastConferma(nome ? `${ruolo.label}: ${nome} · ${client.name}` : `${ruolo.label} rimosso · ${client.name}`);
    lampeggia(client.client_id);
    setBumpTeam({ clientId: client.client_id, ruolo: ruolo.id });
    setTimeout(() => setBumpTeam(null), 500);
  };

  // Handle Call Obiettivo
  const handleEditCallObiettivo = (client) => {
    setEditingCallClient(client);
    setCallObiettivoDate(client.data_call_obiettivo ? client.data_call_obiettivo.split("T")[0] : "");
  };

  // `valore` esplicito solo da "Rimuovi data" (null): altrimenti vale il campo.
  const handleSaveCallObiettivo = async (valore) => {
    if (!editingCallClient) return;
    const data = valore === undefined ? callObiettivoDate : valore;
    setSavingCall(true);
    try {
      const response = await apiFetch(`/clients/${editingCallClient.client_id}/call-obiettivo`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data_call_obiettivo: data || null })
      });
      if (response.ok) {
        toastConferma(data ? `Call obiettivo il ${data.split("-").reverse().join("/")} · ${editingCallClient.name}` : `Call obiettivo rimossa · ${editingCallClient.name}`);
        lampeggia(editingCallClient.client_id);
        setEditingCallClient(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setSavingCall(false);
    }
  };

  // Handle Girato Recupero
  const handleEditGiratoRecupero = (client) => {
    setEditingRecuperoClient(client);
    setRecuperoData({
      has_girato_recupero: client.has_girato_recupero || false,
      data_girato_recupero: client.data_girato_recupero ? client.data_girato_recupero.split("T")[0] : ""
    });
  };

  const handleSaveGiratoRecupero = async () => {
    if (!editingRecuperoClient) return;
    setSavingRecupero(true);
    try {
      const response = await apiFetch(`/clients/${editingRecuperoClient.client_id}/girato-recupero`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(recuperoData)
      });
      if (response.ok) {
        toastConferma(`Girato di recupero aggiornato · ${editingRecuperoClient.name}`);
        lampeggia(editingRecuperoClient.client_id);
        setEditingRecuperoClient(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setSavingRecupero(false);
    }
  };

  // Handle Follower Stats
  const handleEditFollowerStats = (client) => {
    setEditingFollowerClient(client);
    // Get current month
    const now = new Date();
    const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setFollowerData({ instagram: "", tiktok: "", youtube: "", facebook: "", year_month: yearMonth });
    setFollowerStorico([]);
    apiFetch(`/follower-stats/${client.client_id}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((righe) => setFollowerStorico(Array.isArray(righe) ? righe : []))
      .catch(() => setFollowerStorico([]));
  };

  const handleSaveFollowerStats = async () => {
    if (!editingFollowerClient) return;
    setSavingFollower(true);
    try {
      const response = await apiFetch("/follower-stats", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: editingFollowerClient.client_id,
          year_month: followerData.year_month,
          instagram: followerData.instagram ? parseInt(followerData.instagram) : null,
          tiktok: followerData.tiktok ? parseInt(followerData.tiktok) : null,
          youtube: followerData.youtube ? parseInt(followerData.youtube) : null,
          facebook: followerData.facebook ? parseInt(followerData.facebook) : null
        })
      });
      if (response.ok) {
        toastConferma(`Follower salvati · ${editingFollowerClient.name}`);
        lampeggia(editingFollowerClient.client_id);
        setEditingFollowerClient(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingFollower(false);
    }
  };

  // Check if user can see call obiettivo for this client
  // Resp. Operativo / Admin / SMM assegnato / PM assegnato
  const canSeeCallObiettivo = (client) => {
    if (isAdmin || user?.is_responsabile_operativo) return true;
    if (user?.role === "smm" && client.assigned_smm_id === user?.user_id) return true;
    if (isPM && client.assigned_pm_id === user?.user_id) return true;
    return false;
  };

  // Handle editing enabled tasks for a client
  const handleEditEnabledTasks = (client) => {
    setEditingTasksClient(client);
    setEditingEnabledTasks(client.enabled_tasks || ALL_TASK_TYPES.map(t => t.id));
  };

  const handleSaveEnabledTasks = async () => {
    if (!editingTasksClient) return;

    setSavingTasks(true);
    try {
      const response = await apiFetch(`/clients/${editingTasksClient.client_id}/enabled-tasks`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled_tasks: editingEnabledTasks })
      });

      const result = await response.json();

      if (response.ok) {
        toastConferma(`Task aggiornati, ${result.tasks_regenerated} rigenerati · ${editingTasksClient.name}`);
        lampeggia(editingTasksClient.client_id);
        setEditingTasksClient(null);
        fetchData();
      } else {
        toast.error(result.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento dei task");
    } finally {
      setSavingTasks(false);
    }
  };

  const toggleTaskEnabled = (taskId) => {
    setEditingEnabledTasks(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const toggleNewClientTask = (taskId) => {
    setNewClientData(prev => ({
      ...prev,
      enabled_tasks: prev.enabled_tasks.includes(taskId)
        ? prev.enabled_tasks.filter(id => id !== taskId)
        : [...prev.enabled_tasks, taskId]
    }));
  };

  // Handle Servizi per Reparto
  const handleEditServizi = (client) => {
    setEditingServiziClient(client);
    // Inizializza con i dati esistenti o struttura vuota per ogni reparto
    const initialData = {};
    REPARTI.forEach(rep => {
      const existingData = client.servizi_reparto?.[rep.id];
      initialData[rep.id] = {
        servizi: existingData?.servizi || [],
        importo: existingData?.importo || 0,
        enabled: client.reparti?.includes(rep.id) || false
      };
    });
    setServiziRepartoData(initialData);
  };

  const handleSaveServizi = async () => {
    if (!editingServiziClient) return;
    setSavingServizi(true);
    try {
      // Prepara i dati solo per i reparti abilitati
      const serviziReparto = {};
      Object.entries(serviziRepartoData).forEach(([repartoId, data]) => {
        if (data.enabled && (data.servizi.length > 0 || data.importo > 0)) {
          serviziReparto[repartoId] = {
            servizi: data.servizi,
            importo: data.importo || 0
          };
        }
      });

      const response = await apiFetch(`/clients/${editingServiziClient.client_id}/servizi-reparto`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ servizi_reparto: serviziReparto })
      });
      if (response.ok) {
        const result = await response.json();
        toastConferma(`Servizi aggiornati, totale ${formatCurrency(result.importo_totale, { decimals: 0 })} · ${editingServiziClient.name}`);
        lampeggia(editingServiziClient.client_id);
        setEditingServiziClient(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento dei servizi");
    } finally {
      setSavingServizi(false);
    }
  };

  const toggleRepartoEnabled = (repartoId) => {
    setServiziRepartoData(prev => ({
      ...prev,
      [repartoId]: {
        ...prev[repartoId],
        enabled: !prev[repartoId]?.enabled
      }
    }));
  };

  const toggleServizioReparto = (repartoId, servizioId) => {
    setServiziRepartoData(prev => {
      const currentServizi = prev[repartoId]?.servizi || [];
      // Gestione speciale per social organico: solo uno può essere selezionato
      if (servizioId === "gmt_social_organico_8" || servizioId === "gmt_social_organico_12") {
        const otherOption = servizioId === "gmt_social_organico_8" ? "gmt_social_organico_12" : "gmt_social_organico_8";
        const newServizi = currentServizi.includes(servizioId)
          ? currentServizi.filter(id => id !== servizioId)
          : [...currentServizi.filter(id => id !== otherOption), servizioId];
        return {
          ...prev,
          [repartoId]: { ...prev[repartoId], servizi: newServizi }
        };
      }
      // Toggle normale
      const newServizi = currentServizi.includes(servizioId)
        ? currentServizi.filter(id => id !== servizioId)
        : [...currentServizi, servizioId];
      return {
        ...prev,
        [repartoId]: { ...prev[repartoId], servizi: newServizi }
      };
    });
  };

  const updateImportoReparto = (repartoId, importo) => {
    setServiziRepartoData(prev => ({
      ...prev,
      [repartoId]: {
        ...prev[repartoId],
        importo: parseFloat(importo) || 0
      }
    }));
  };

  // Calculate total from all reparti
  const getTotaleImporto = () => {
    return Object.values(serviziRepartoData).reduce((total, data) => {
      return total + (data.enabled ? (data.importo || 0) : 0);
    }, 0);
  };

  // Handle Delete Client
  const handleDeleteClient = async () => {
    if (!deletingClient || deleteConfirmName !== deletingClient.name) {
      toast.error("Il nome inserito non corrisponde");
      return;
    }
    setDeleting(true);
    try {
      const response = await apiFetch(`/clients/${deletingClient.client_id}`, {
        method: "DELETE"
      });
      if (response.ok) {
        toast.success("Cliente eliminato con successo");
        setDeletingClient(null);
        setDeleteConfirmName("");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione del cliente");
    } finally {
      setDeleting(false);
    }
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleToggleClientActive = async (clientId) => {
    setCambioStatoSaving(true);
    try {
      const response = await apiFetch(`/clients/${clientId}/toggle-active`, {
        method: "PATCH"
      });
      if (response.ok) {
        const result = await response.json();
        const nome = clients.find((c) => c.client_id === clientId)?.name || "Cliente";
        toastConferma(result.is_active ? `${nome} riattivato` : `${nome} spostato in Inattivi`);
        lampeggia(clientId);
        fetchData();
      } else {
        toast.error("Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setCambioStatoSaving(false);
      setClienteDaCambiareStato(null);
    }
  };

  // Separa clienti attivi e inattivi
  // Funzione comparatore in base a sortBy
  const sortClients = (list) => {
    const arr = [...list];
    const collator = COLLATOR_NOMI;
    const safeName = (c) => (c.name || "").trim();
    const safeDate = (c) => c.data_girato ? new Date(c.data_girato).getTime() : 0;
    // Stessa cifra mostrata nella colonna € mensile.
    const safeAmount = (c) => importoDelMese(c, canoneServizi(c)) + quotaDaSommare(c, tiktokFees[c.client_id]);
    
    switch (sortBy) {
      case "name_desc":
        arr.sort((a, b) => collator.compare(safeName(b), safeName(a)));
        break;
      case "data_girato_asc":
        arr.sort((a, b) => safeDate(a) - safeDate(b));
        break;
      case "data_girato_desc":
        arr.sort((a, b) => safeDate(b) - safeDate(a));
        break;
      case "importo_desc":
        arr.sort((a, b) => safeAmount(b) - safeAmount(a));
        break;
      case "name_asc":
      default:
        arr.sort((a, b) => collator.compare(safeName(a), safeName(b)));
    }
    return arr;
  };

  // Chi ha firmato ma non ha pagato lo start (prima fattura) non è ancora
  // operativo: resta nella pagina Contratti con l'avviso, non qui, dove
  // sembrerebbe un cliente da seguire (GET /clients, in_attesa_avvio).
  const clientiInAttesaAvvio = clients.filter(client => client.is_active !== false && client.in_attesa_avvio);
  const activeClients = sortClients(clients.filter(client => client.is_active !== false && !client.in_attesa_avvio));
  const inactiveClients = sortClients(clients.filter(client => client.is_active === false));

  // Un unico criterio per attivi e inattivi: ricerca, persone del team e stato
  // del cliente (pagato, servizio, contratto, team completo).
  const giorniAllaScadenza = (c) =>
    c.contratto_data_scadenza ? Math.ceil((new Date(c.contratto_data_scadenza) - new Date()) / 86400000) : null;
  const assegnatiTeam = (c) =>
    [c.assigned_pm_id, c.assigned_smm_id || c.smm_tiktok_id || c.smm_social_id, c.videomaker_id, c.editor_id].filter(Boolean).length;
  // Opzioni del filtro Servizio: i servizi del Catalogo attaccati ad almeno un
  // cliente, letti dai pacchetti. I vecchi tag servizi_reparto (Social,
  // TikTok Shop) non li scrive più nessuno: filtrare su quelli lasciava fuori
  // tutti i clienti con un servizio del Catalogo.
  const serviziInUso = (() => {
    const perId = new Map();
    clients.forEach((c) => (c.pacchetti || []).forEach((pk) => {
      if (pk.servizio_id && !perId.has(pk.servizio_id)) perId.set(pk.servizio_id, pk.nome || "Servizio");
    }));
    return [...perId].map(([id, nome]) => ({ id, nome })).sort((a, b) => COLLATOR_NOMI.compare(a.nome, b.nome));
  })();

  const passaFiltri = (client) => {
    const matchesSearch = client.name?.toLowerCase().includes(ricercaDifferita.toLowerCase());
    const matchesPM = filterPM === "all" || client.assigned_pm_id === filterPM;
    const matchesSMM = filterSMM === "all" ||
      client.assigned_smm_id === filterSMM ||
      client.smm_tiktok_id === filterSMM ||
      client.smm_social_id === filterSMM;
    const matchesCC = filterCC === "all" || client.videomaker_id === filterCC;
    const matchesEditor = filterEditor === "all" || client.editor_id === filterEditor;
    const pagato = client.ha_pagato !== false;
    const matchesPagato = filterPagato === "all"
      || (filterPagato === "pagato" && pagato)
      || (filterPagato === "non_pagato" && !pagato)
      || (filterPagato === "arretrati" && (client.stato_pagamenti?.arretrati?.length || 0) > 0);
    const pacchetti = client.pacchetti || [];
    const matchesServizio = filterServizio === "all"
      || (filterServizio === "nessuno" && pacchetti.length === 0)
      || pacchetti.some((pk) => pk.servizio_id === filterServizio);
    const gg = giorniAllaScadenza(client);
    const matchesContratto = filterContratto === "all"
      || (filterContratto === "in_scadenza" && gg !== null && gg >= 0 && gg <= 30)
      || (filterContratto === "scaduto" && gg !== null && gg < 0)
      || (filterContratto === "senza" && gg === null);
    const n = assegnatiTeam(client);
    const matchesTeam = filterTeam === "all" || (filterTeam === "completo" ? n === 4 : n < 4);
    // Percentuale = almeno un addon mensile a percentuale sul fatturato;
    // "solo fisso" = mensili tutti a importo (chi non ha mensili resta fuori).
    const canone = canoneServizi(client);
    const matchesMensile = filterMensile === "all"
      || (filterMensile === "percentuale" && canone.percentuale > 0)
      || (filterMensile === "fisso" && canone.haMensili && canone.percentuale === 0);
    return matchesSearch && matchesPM && matchesSMM && matchesCC && matchesEditor
      && matchesPagato && matchesServizio && matchesContratto && matchesTeam && matchesMensile;
  };

  const filteredClients = activeClients.filter(passaFiltri);
  const filteredInactiveClients = inactiveClients.filter(passaFiltri);

  const filtriAttivi = [filterPM, filterSMM, filterCC, filterEditor, filterPagato, filterServizio, filterContratto, filterTeam, filterMensile]
    .filter((v) => v !== "all").length + (searchQuery ? 1 : 0);
  const azzeraFiltri = () => {
    setSearchQuery("");
    [setFilterPM, setFilterSMM, setFilterCC, setFilterEditor, setFilterPagato, setFilterServizio, setFilterContratto, setFilterTeam, setFilterMensile]
      .forEach((set) => set("all"));
  };

  const getSmmName = (smmId) => {
    const smm = smmUsers.find(u => u.user_id === smmId);
    return smm?.name || "Non assegnato";
  };

  if (loading) return <SkeletonClienti />;

  return (
    <div className="flex flex-col gap-[18px] animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 scheda-font-ui">
        <div>
          <h1 className="text-[30px] font-bold tracking-[-.02em] text-[#15161c]">
            Clienti
          </h1>
          <p className="text-[13.5px] text-[#565d6d] mt-0.5">
            {clients.length} clienti totali
          </p>
        </div>

        {(isPM || isAdmin || isAMM || user?.is_responsabile_operativo) && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setNewClientDialogOpen(true)}
              data-testid="new-client-btn"
              className="h-10 px-4 rounded-[12px] border border-[#c9c9f0] bg-white text-[#5b5bd6] text-[13.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-[#eeeefc] transition-colors scheda-font-ui"
              style={{ boxShadow: SHADOWS.appoggiato }}
            >
              <Plus className="w-4 h-4" />
              Nuovo cliente
            </button>
            <Dialog open={importDialogOpen} onOpenChange={(open) => { setImportDialogOpen(open); if (!open) resetImportWizard(); }}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  data-testid="import-btn"
                  className={`${PRIMARY_BTN} h-10 rounded-[12px] text-[13.5px]`}
                  style={{ boxShadow: SHADOWS.accento }}
                >
                  <Upload className="w-4 h-4" />
                  Importa Excel
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">
                    {importStep === "upload" && "Carica file Excel"}
                    {importStep === "mapping" && "Mappa le colonne ai campi cliente"}
                    {importStep === "review" && "Anteprima modifiche — Verifica prima di confermare"}
                    {importStep === "done" && "Import completato"}
                  </DialogTitle>
                </DialogHeader>

                {/* STEP 1: Upload */}
                {importStep === "upload" && (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Carica il tuo file .xlsx — al passo successivo potrai indicare quale colonna corrisponde a ciascun campo cliente.
                    </p>
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-10 text-center">
                      <FileSpreadsheet className="w-14 h-14 mx-auto text-muted-foreground mb-4" />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="excel-upload"
                        data-testid="excel-upload-input"
                        disabled={importLoading}
                      />
                      <label
                        htmlFor="excel-upload"
                        className={`cursor-pointer text-indigo-600 hover:text-indigo-700 font-medium ${importLoading ? "opacity-50 pointer-events-none" : ""}`}
                      >
                        {importLoading ? "Analisi in corso..." : "Clicca per selezionare il file .xlsx"}
                      </label>
                    </div>
                  </div>
                )}

                {/* STEP 2: Mapping */}
                {importStep === "mapping" && importAnalysis && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3">
                      <div>
                        <p className="font-medium text-sm">{importAnalysis.filename}</p>
                        <p className="text-xs text-muted-foreground">{importAnalysis.total_rows} righe · {importAnalysis.columns.length} colonne</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={resetImportWizard} data-testid="change-file-btn">Cambia file</Button>
                    </div>

                    {/* Selettore foglio - sempre visibile per chiarezza */}
                    {importAnalysis.sheet_names && importAnalysis.sheet_names.length >= 1 && (
                      <div className="flex items-center gap-3 p-3 border border-purple-200 rounded-lg bg-purple-50">
                        <Label className="text-sm font-semibold text-purple-700 whitespace-nowrap">📑 Foglio:</Label>
                        <Select
                          value={importAnalysis.current_sheet}
                          onValueChange={handleSwitchSheet}
                          disabled={importLoading || importAnalysis.sheet_names.length < 2}
                        >
                          <SelectTrigger className="w-64" data-testid="sheet-selector">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {importAnalysis.sheet_names.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-xs text-muted-foreground">
                          {importAnalysis.sheet_names.length === 1
                            ? "Il file ha un solo foglio"
                            : `${importAnalysis.sheet_names.length} fogli totali — scegli il foglio da importare`}
                        </span>
                      </div>
                    )}

                    {/* Selettore riga intestazione */}
                    <div className="flex items-center gap-3 p-3 border border-blue-200 rounded-lg bg-blue-50">
                      <Label className="text-sm font-semibold text-blue-700 whitespace-nowrap">📋 Riga intestazione:</Label>
                      <Select
                        value={String((importAnalysis.header_row ?? 0) + 1)}
                        onValueChange={(v) => handleSwitchSheet(importAnalysis.current_sheet, parseInt(v, 10) - 1)}
                        disabled={importLoading}
                      >
                        <SelectTrigger className="w-32" data-testid="header-row-selector">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1,2,3,4,5,6,7,8,9,10].map(n => (
                            <SelectItem key={n} value={String(n)}>Riga {n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-xs text-muted-foreground">
                        Se vedi colonne "Unnamed: 0/1/2..." significa che le vere intestazioni sono in una riga diversa
                      </span>
                    </div>

                    {/* Preview tabella prime 5 righe */}
                    <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-48">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            {importAnalysis.columns.map(c => (
                              <th key={c} className="p-2 text-left font-semibold text-muted-foreground whitespace-nowrap">{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {importAnalysis.preview.map((row, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              {importAnalysis.columns.map(c => (
                                <td key={c} className="p-2 whitespace-nowrap text-foreground">{row[c] || "-"}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mapping campi → colonne */}
                    <div className="space-y-2">
                      <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Mappatura campi</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {importAnalysis.fields.map(f => (
                          <div key={f.key} className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {f.label}
                                {f.required && <span className="text-red-500 ml-1">*</span>}
                              </p>
                              <p className="text-xs text-muted-foreground">campo cliente</p>
                            </div>
                            <Select
                              value={importMapping[f.key] || "__none__"}
                              onValueChange={(v) => {
                                setImportMapping(prev => {
                                  const next = { ...prev };
                                  if (v === "__none__") delete next[f.key];
                                  else next[f.key] = v;
                                  return next;
                                });
                              }}
                            >
                              <SelectTrigger className="w-52" data-testid={`map-${f.key}`}>
                                <SelectValue placeholder="Seleziona colonna..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">— Non mappato —</SelectItem>
                                {importAnalysis.columns.map(c => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                      <p className="text-xs text-muted-foreground">
                        {Object.keys(importMapping).length} di {importAnalysis.fields.length} campi mappati
                      </p>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={resetImportWizard} disabled={importLoading}>Annulla</Button>
                        <Button
                          onClick={handlePreviewImport}
                          disabled={importLoading || !importMapping.name}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          data-testid="preview-import-btn"
                        >
                          {importLoading ? "Caricamento..." : "Anteprima modifiche →"}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3: Review (anteprima modifiche) */}
                {importStep === "review" && importPreview && (
                  <div className="space-y-4">
                    {/* Riepilogo conteggi */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                        <p className="text-xs uppercase tracking-wider text-emerald-700">Nuovi</p>
                        <p className="text-2xl font-bold text-emerald-700">{importPreview.new_count}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <p className="text-xs uppercase tracking-wider text-amber-700">Da aggiornare</p>
                        <p className="text-2xl font-bold text-amber-700">{importPreview.updated_count}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <p className="text-xs uppercase tracking-wider text-slate-600">Invariati</p>
                        <p className="text-2xl font-bold text-slate-600">{importPreview.unchanged_count}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                        <p className="text-xs uppercase tracking-wider text-blue-700">Non nel file</p>
                        <p className="text-2xl font-bold text-blue-700">{importPreview.not_in_file_count}</p>
                      </div>
                    </div>

                    <div className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
                      ℹ️ I clienti "Non nel file" <strong>NON verranno eliminati</strong>: vengono solo segnalati per tua informazione.
                    </div>

                    {/* Nuovi clienti */}
                    {importPreview.new_count > 0 && (
                      <details className="border border-emerald-200 rounded-lg" open>
                        <summary className="cursor-pointer p-3 bg-emerald-50 font-medium text-sm">
                          ✨ {importPreview.new_count} nuovi clienti
                        </summary>
                        <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                          {importPreview.new_clients.map((c, i) => (
                            <div key={i} className="text-xs px-2 py-1 border-b border-emerald-100 last:border-0">
                              <strong>{c.name}</strong>
                              {Object.keys(c.fields).filter(k => k !== "name").length > 0 && (
                                <span className="text-muted-foreground ml-2">
                                  ({Object.keys(c.fields).filter(k => k !== "name").length} campi compilati)
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Aggiornati con diff */}
                    {importPreview.updated_count > 0 && (
                      <details className="border border-amber-200 rounded-lg" open>
                        <summary className="cursor-pointer p-3 bg-amber-50 font-medium text-sm">
                          🔄 {importPreview.updated_count} clienti da aggiornare (mostrate prime 200)
                        </summary>
                        <div className="max-h-72 overflow-y-auto p-2 space-y-2">
                          {importPreview.updated_clients.map((c, i) => (
                            <div key={i} className="text-xs p-2 border border-amber-100 rounded">
                              <p className="font-semibold mb-1">{c.name}</p>
                              <div className="space-y-0.5 pl-2">
                                {c.changes.map((ch, j) => (
                                  <div key={j} className="flex items-center gap-2 text-[11px]">
                                    <span className="font-medium text-muted-foreground min-w-[140px]">{ch.field}:</span>
                                    <span className="text-red-600 line-through">{ch.old}</span>
                                    <span className="text-muted-foreground">→</span>
                                    <span className="text-emerald-700 font-semibold">{ch.new}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Non nel file */}
                    {importPreview.not_in_file_count > 0 && (
                      <details className="border border-blue-200 rounded-lg">
                        <summary className="cursor-pointer p-3 bg-blue-50 font-medium text-sm">
                          📋 {importPreview.not_in_file_count} clienti in DB ma non nel file (non saranno toccati)
                        </summary>
                        <div className="max-h-40 overflow-y-auto p-2 space-y-1">
                          {importPreview.not_in_file.map((c, i) => (
                            <div key={i} className="text-xs px-2 py-1 text-muted-foreground">{c.name}</div>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Errori */}
                    {importPreview.errors?.length > 0 && (
                      <details className="border border-red-200 rounded-lg">
                        <summary className="cursor-pointer p-3 bg-red-50 font-medium text-sm text-red-700">
                          ⚠️ {importPreview.errors.length} errori durante la lettura
                        </summary>
                        <div className="max-h-40 overflow-y-auto p-2 space-y-1">
                          {importPreview.errors.map((err, i) => (
                            <div key={i} className="text-xs px-2 py-1 text-muted-foreground">Riga {err.row}: {err.error}</div>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Azioni */}
                    <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                      <Button variant="outline" onClick={() => setImportStep("mapping")} disabled={importLoading}>
                        ← Torna alla mappatura
                      </Button>
                      <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => { setImportDialogOpen(false); resetImportWizard(); }} disabled={importLoading}>
                          Annulla import
                        </Button>
                        <Button
                          onClick={handleConfirmImport}
                          disabled={importLoading || (importPreview.new_count === 0 && importPreview.updated_count === 0)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          data-testid="confirm-import-btn"
                        >
                          {importLoading ? "Importazione..." : `✓ Conferma e applica (${importPreview.new_count + importPreview.updated_count} clienti)`}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 4: Done */}
                {importStep === "done" && importResult && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      <span><strong>{importResult.imported}</strong> nuovi clienti importati</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-5 h-5 text-amber-500" />
                      <span><strong>{importResult.updated}</strong> clienti aggiornati</span>
                    </div>
                    {importResult.errors?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-destructive flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {importResult.total_errors || importResult.errors.length} errori
                        </p>
                        <div className="max-h-40 overflow-y-auto mt-1 border border-slate-200 rounded-md p-2 bg-slate-50">
                          {importResult.errors.map((err, i) => (
                            <p key={i} className="text-xs text-muted-foreground">
                              Riga {err.row}: {err.error}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" onClick={resetImportWizard}>Importa altro</Button>
                      <Button onClick={() => { setImportDialogOpen(false); resetImportWizard(); }} className="bg-indigo-600 hover:bg-indigo-700 text-white">Chiudi</Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* New Client Dialog */}
      <Dialog open={newClientDialogOpen} onOpenChange={setNewClientDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuovo Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="client-name">Nome Cliente *</Label>
              <Input
                id="client-name"
                placeholder="Es. Mario Rossi"
                value={newClientData.name}
                onChange={(e) => setNewClientData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="new-client-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client-data-girato">Data Girato</Label>
                <Input
                  id="client-data-girato"
                  type="date"
                  value={newClientData.data_girato}
                  onChange={(e) => setNewClientData(prev => ({ ...prev, data_girato: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client-crescita">% Crescita</Label>
                <Input
                  id="client-crescita"
                  type="number"
                  placeholder="Es. 15"
                  value={newClientData.percentuale_crescita}
                  onChange={(e) => setNewClientData(prev => ({ ...prev, percentuale_crescita: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client-smm">Assegna a SMM</Label>
              <Select
                value={newClientData.assigned_smm_id || "none"}
                onValueChange={(value) => setNewClientData(prev => ({ ...prev, assigned_smm_id: value === "none" ? "" : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona SMM..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assegnato</SelectItem>
                  {smmUsers.map(smm => (
                    <SelectItem key={smm.user_id} value={smm.user_id}>
                      {smm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client-pm">Assegna a PM</Label>
              <Select
                value={newClientData.assigned_pm_id || "none"}
                onValueChange={(value) => setNewClientData(prev => ({ ...prev, assigned_pm_id: value === "none" ? "" : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona PM..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assegnato</SelectItem>
                  {pmUsers.map(pm => (
                    <SelectItem key={pm.user_id} value={pm.user_id}>
                      {pm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client-importo">Importo Mensile (€)</Label>
              <Input
                id="client-importo"
                type="number"
                step="0.01"
                placeholder="Es: 1500"
                value={newClientData.importo_mensile}
                onChange={(e) => setNewClientData(prev => ({ ...prev, importo_mensile: e.target.value }))}
              />
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Settings2 className="w-4 h-4" />
                Task Abilitati
              </Label>
              <p className="text-xs text-muted-foreground">
                Seleziona i task che verranno generati per questo cliente
              </p>
              <div className="grid grid-cols-1 gap-2 p-3 bg-slate-50 rounded-lg">
                {ALL_TASK_TYPES.map(task => (
                  <div key={task.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`new-task-${task.id}`}
                      checked={newClientData.enabled_tasks.includes(task.id)}
                      onCheckedChange={() => toggleNewClientTask(task.id)}
                    />
                    <label
                      htmlFor={`new-task-${task.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {task.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setNewClientDialogOpen(false)}>
                Annulla
              </Button>
              <Button
                onClick={handleCreateClient}
                disabled={savingClient || !newClientData.name.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="save-new-client-btn"
              >
                {savingClient ? "Creazione..." : "Crea Cliente"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modali della lista (components/ModaliClienti.jsx): solo presentazione,
          stato e salvataggi restano qui sotto negli handler. */}
      <ModaleTask
        client={editingTasksClient}
        onClose={() => setEditingTasksClient(null)}
        tuttiTask={ALL_TASK_TYPES}
        abilitati={editingEnabledTasks}
        setAbilitati={setEditingEnabledTasks}
        saving={savingTasks}
        onSave={handleSaveEnabledTasks}
      />
      <ModaleGirati
        client={giratiClient}
        onClose={() => setGiratiClient(null)}
        onCambiato={() => { lampeggia(giratiClient?.client_id); fetchData(); }}
      />
      <ModaleDatiAmministrativi
        client={editingAdminClient}
        onClose={() => setEditingAdminClient(null)}
        dati={adminData}
        setDati={setAdminData}
        puoRinominare={isAdmin}
        saving={savingAdmin}
        onSave={handleSaveAdminData}
      />
      <ModaleCallObiettivo
        client={editingCallClient}
        onClose={() => setEditingCallClient(null)}
        data={callObiettivoDate}
        setData={setCallObiettivoDate}
        saving={savingCall}
        onSave={() => handleSaveCallObiettivo()}
        onRimuovi={() => handleSaveCallObiettivo(null)}
      />
      <ModaleGiratoRecupero
        client={editingRecuperoClient}
        onClose={() => setEditingRecuperoClient(null)}
        dati={recuperoData}
        setDati={setRecuperoData}
        saving={savingRecupero}
        onSave={handleSaveGiratoRecupero}
      />
      <ModaleFollower
        client={editingFollowerClient}
        onClose={() => setEditingFollowerClient(null)}
        dati={followerData}
        setDati={setFollowerData}
        storico={followerStorico}
        saving={savingFollower}
        onSave={handleSaveFollowerStats}
      />
      <ModaleServizi
        client={editingServiziClient}
        onClose={() => setEditingServiziClient(null)}
        reparti={REPARTI}
        serviziPerReparto={SERVIZI_PER_REPARTO}
        dati={serviziRepartoData}
        onToggleReparto={toggleRepartoEnabled}
        onToggleServizio={toggleServizioReparto}
        onImporto={updateImportoReparto}
        totale={getTotaleImporto()}
        saving={savingServizi}
        onSave={handleSaveServizi}
      />


      {/* Barra filtri in vetro: tab Attivi/Inattivi, ricerca, ordinamento, filtri team */}
      {/* sticky: resta agganciata in alto mentre si scorre la lista. Il contenitore
          che scorre è <main> (App.js), e fra i due non ci sono overflow che la blocchino. */}
      <div
        className="sticky top-3 z-30 flex items-center gap-2 flex-wrap p-2.5 rounded-[18px] bg-white/[.82] border border-[rgba(233,233,241,.9)] backdrop-blur-[14px] scheda-font-ui"
        style={{ boxShadow: SHADOWS.card }}
      >
        <div className="relative inline-flex p-[3px] rounded-[12px] bg-[#f3f3f8] gap-0.5" role="tablist">
          {/* Indicatore bianco che scorre sotto il tab scelto. */}
          <span
            aria-hidden="true"
            className="absolute top-[3px] bottom-[3px] rounded-[10px] bg-white transition-[left,width] duration-300 ease-[cubic-bezier(.22,.7,.3,1)] motion-reduce:transition-none"
            style={{ boxShadow: SHADOWS.appoggiato, left: indicatoreTab.left, width: indicatoreTab.width }}
          />
          {[
            ["attivi", "Attivi", filteredClients.length, "#9095a1", "active-clients-tab"],
            ["inattivi", "Inattivi", filteredInactiveClients.length, "#b45309", "inactive-clients-folder"],
          ].map(([id, label, n, colore, testId]) => {
            const sel = vistaClienti === id;
            return (
              <button
                key={id}
                ref={(el) => { tabRefs.current[id] = el; }}
                type="button"
                role="tab"
                aria-selected={sel}
                onClick={() => {
                  if (sel) return;
                  setDirezioneVista(id === "inattivi" ? 1 : -1);
                  setVistaClienti(id);
                  setSelectedClients([]);
                }}
                className={`relative z-[1] h-8 px-3 rounded-[10px] inline-flex items-center gap-1.5 text-[13px] font-semibold transition-colors duration-300 ${sel ? "text-[#15161c]" : "text-[#6b7280] hover:text-[#15161c]"}`}
                data-testid={testId}
              >
                {label}
                <span className="scheda-font-mono text-[12px]" style={{ color: colore }}>{n}</span>
              </button>
            );
          })}
        </div>

        <div className="relative order-last md:order-none basis-full md:basis-auto flex-1 min-w-[160px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9095a1] pointer-events-none" />
          <input
            data-testid="search-clients"
            placeholder="Cerca cliente..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`${FIELD_CLS} w-full h-[38px] pl-9 shadow-[inset_0_1px_2px_rgba(15,23,42,.04)] focus:-translate-y-px transition-[box-shadow,transform]`}
          />
        </div>

        <button
          type="button"
          onClick={() => setFiltriMobileAperti((v) => !v)}
          className={`md:hidden ml-auto h-[38px] px-3 rounded-[10px] border text-[13px] font-semibold inline-flex items-center gap-1.5 transition-colors ${filtriMobileAperti || filtriAttivi > 0 ? "border-[#c9c9f0] bg-[#f7f7fe] text-[#5b5bd6]" : "border-[#e0e0ea] bg-white text-[#3c414d]"}`}
          aria-expanded={filtriMobileAperti}
          data-testid="toggle-filtri-mobile"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filtri
          {filtriAttivi > 0 && (
            <span className="scheda-font-mono text-[11.5px] h-5 min-w-5 px-1 rounded-full bg-[#eeeefc] inline-flex items-center justify-center">{filtriAttivi}</span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${filtriMobileAperti ? "rotate-180" : ""}`} />
        </button>

        {/* Su desktop il wrapper sparisce (contents) e i filtri restano in riga;
            su mobile diventa una griglia a due colonne che si apre col pulsante. */}
        <div className={`${filtriMobileAperti ? "grid" : "hidden"} order-last basis-full grid-cols-2 gap-2 md:contents`}>
        <FiltroSelect value={sortBy} onChange={setSortBy} attivo={false} testId="sort-clients" placeholder="Ordina per...">
          <SelectItem value="name_asc">Nome A → Z</SelectItem>
          <SelectItem value="name_desc">Nome Z → A</SelectItem>
          <SelectItem value="importo_desc">€ mensile ↓</SelectItem>
          <SelectItem value="data_girato_desc">Data girato (più recente)</SelectItem>
          <SelectItem value="data_girato_asc">Data girato (più vecchia)</SelectItem>
        </FiltroSelect>

        {(isAdmin || user?.is_responsabile_operativo || isPM || isAMM) && (
          <>
            {[
              ["PM", filterPM, setFilterPM, pmUsers, "filter-pm"],
              ["SMM", filterSMM, setFilterSMM, smmUsers, "filter-smm"],
              ["CC", filterCC, setFilterCC, videomakeUsers, "filter-cc"],
              ["Editor", filterEditor, setFilterEditor, editorUsers, "filter-editor"],
            ].map(([label, value, setValue, lista, testId]) => (
              <FiltroSelect key={testId} value={value} onChange={setValue} attivo={value !== "all"} testId={testId}>
                <SelectItem value="all">{label}: tutti</SelectItem>
                {lista.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                ))}
              </FiltroSelect>
            ))}
          </>
        )}

        {/* Filtri sullo stato del cliente. Pagato e Team solo a chi vede quelle colonne. */}
        {canEditAdmin && (
          <FiltroSelect value={filterPagato} onChange={setFilterPagato} attivo={filterPagato !== "all"} testId="filter-pagato">
            <SelectItem value="all">Pagato: tutti</SelectItem>
            <SelectItem value="pagato">Pagato</SelectItem>
            <SelectItem value="non_pagato">Non pagato</SelectItem>
            <SelectItem value="arretrati">Con arretrati</SelectItem>
          </FiltroSelect>
        )}
        <FiltroSelect value={filterServizio} onChange={setFilterServizio} attivo={filterServizio !== "all"} testId="filter-servizio">
          <SelectItem value="all">Servizio: tutti</SelectItem>
          {serviziInUso.map((sv) => (
            <SelectItem key={sv.id} value={sv.id}>{sv.nome}</SelectItem>
          ))}
          <SelectItem value="nessuno">Nessun servizio</SelectItem>
        </FiltroSelect>
        <FiltroSelect value={filterContratto} onChange={setFilterContratto} attivo={filterContratto !== "all"} testId="filter-contratto">
          <SelectItem value="all">Contratto: tutti</SelectItem>
          <SelectItem value="in_scadenza">In scadenza (30 gg)</SelectItem>
          <SelectItem value="scaduto">Scaduto</SelectItem>
          <SelectItem value="senza">Senza contratto</SelectItem>
        </FiltroSelect>
        {canEditAdmin && (
          <FiltroSelect value={filterTeam} onChange={setFilterTeam} attivo={filterTeam !== "all"} testId="filter-team">
            <SelectItem value="all">Team: tutti</SelectItem>
            <SelectItem value="completo">Completo (4/4)</SelectItem>
            <SelectItem value="incompleto">Incompleto</SelectItem>
          </FiltroSelect>
        )}
        {canEditAdmin && (
          <div className="relative">
            <FiltroSelect value={filterMensile} onChange={setFilterMensile} attivo={filterMensile !== "all"} testId="filter-mensile">
              <SelectItem value="all">Mensile: tutti</SelectItem>
              <SelectItem value="percentuale">Con percentuale</SelectItem>
              <SelectItem value="fisso">Solo fisso</SelectItem>
            </FiltroSelect>
            {/* Filtro rilasciato il 2/10/2026: "New" per 10 giorni. */}
            <BadgeNew fino="2026-10-12" floating testId="filter-mensile-new" />
          </div>
        )}
        </div>
        {filtriAttivi > 0 && (
          <button
            type="button"
            onClick={azzeraFiltri}
            className="h-[38px] px-3 rounded-[10px] text-[12.5px] font-semibold text-[#5b5bd6] hover:bg-[#eeeefc] inline-flex items-center gap-1.5 transition-colors clienti-pop"
            data-testid="reset-filters"
          >
            <X className="w-3.5 h-3.5" />
            Azzera filtri
            <span className="scheda-font-mono text-[11.5px] h-5 min-w-5 px-1 rounded-full bg-[#eeeefc] inline-flex items-center justify-center">{filtriAttivi}</span>
          </button>
        )}
      </div>

      {vistaClienti === "attivi" && clientiInAttesaAvvio.length > 0 && (
        <InfoTooltip
          title="In attesa dello start"
          description={`Hanno firmato il contratto ma non hanno ancora pagato la prima fattura: ${clientiInAttesaAvvio.map((c) => c.name).join(", ")}. Compaiono fra i clienti quando l'amministrazione registra il pagamento.`}
        >
          <button
            type="button"
            onClick={() => navigate("/contratti")}
            className="self-start inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#b45309] hover:underline scheda-font-ui"
            data-testid="clienti-in-attesa-avvio"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            {clientiInAttesaAvvio.length === 1
              ? "1 cliente ha firmato ma non ha pagato lo start: è nei Contratti"
              : `${clientiInAttesaAvvio.length} clienti hanno firmato ma non hanno pagato lo start: sono nei Contratti`}
          </button>
        </InfoTooltip>
      )}

      {/* Tabella: stessa per attivi e inattivi. Le colonne dipendono dal ruolo. */}
      {(() => {
        const inattivi = vistaClienti === "inattivi";
        const lista = inattivi ? filteredInactiveClients : filteredClients;
        // Larghezze del prototipo ridotte di poco: il prototipo aveva 1340px, qui
        // accanto alla sidebar lo spazio utile è ~1110px. Sotto, scroll orizzontale.
        const colonne = [
          isPM && "44px",
          "minmax(240px,1fr)",
          "170px",
          canEditAdmin && "68px",
          canEditAdmin && "120px",
          isAMM && "128px",
          canCambiareStato && "118px",
          "152px",
        ].filter(Boolean).join("_");
        // Su mobile ogni riga è una card a capo (flex-wrap) e le colonne non
        // esistono: le gridTemplateColumns inline non hanno effetto finché il
        // contenitore è flex. Da md in su torna la griglia della tabella.
        const gridCls = `flex flex-wrap items-start gap-x-3 gap-y-0 md:grid md:gap-3 md:items-center px-4 md:px-[18px]`;
        // Il salto di riga porta anche lo spazio verticale (gap-y-0 sul contenitore):
        // con un gap, la riga vuota del salto lo raddoppierebbe.
        const aCapo = <div className="basis-full h-3 md:hidden" aria-hidden="true" />;
        const gridStyle = { gridTemplateColumns: colonne.split("_").join(" ") };
        const idsLista = lista.map((c) => c.client_id);
        const nSel = idsLista.filter((id) => selectedClients.includes(id)).length;
        const statoTutti = nSel === 0 ? false : nSel === idsLista.length ? "tutti" : "parziale";
        const liste = { pm: pmUsers, smm: smmUsers, cc: videomakeUsers, editor: editorUsers };
        const nTask = ALL_TASK_TYPES.length;
        const iconBtn = "w-8 h-8 rounded-[9px] inline-flex items-center justify-center transition-colors";

        return (
          <div
            key={vistaClienti}
            className={`bg-white rounded-[18px] border border-[#e9e9f1] overflow-hidden scheda-font-ui ${direzioneVista > 0 ? "clienti-vista-da-destra" : "clienti-vista-da-sinistra"}`}
            style={{ boxShadow: SHADOWS.card }}
          >
            <div className="md:overflow-x-auto">
              <div className={isAMM ? "md:min-w-[1148px]" : "md:min-w-[1008px]"}>
                <div className={`${gridCls} !items-center py-3 bg-[#fbfbfd] text-[11px] font-semibold uppercase tracking-[.06em] text-[#9095a1]`} style={gridStyle}>
                  {isPM && (
                    <CasellaSelezione
                      stato={statoTutti}
                      onClick={() => setSelectedClients(statoTutti === "tutti" ? [] : idsLista)}
                      testId="select-all-checkbox"
                      label="Seleziona tutti"
                    />
                  )}
                  <span>Cliente</span>
                  <span className="hidden md:block">Date</span>
                  {canEditAdmin && <span className="hidden md:block">Team</span>}
                  {canEditAdmin && <span className="hidden md:block text-right">€ Mensile</span>}
                  {isAMM && <span className="hidden md:block">Fatturazione</span>}
                  {canCambiareStato && <span className="hidden md:block">Stato</span>}
                  <span className="hidden md:block text-right">Azioni</span>
                </div>

                {lista.length === 0 ? (
                  <div className="border-t border-[#f0f0f6] py-10 text-center text-[13.5px] text-[#9095a1]">
                    {inattivi ? "Nessun cliente inattivo" : "Nessun cliente trovato"}
                  </div>
                ) : (
                  lista.slice(0, righeLimite).map((client, index) => {
                    const selezionato = selectedClients.includes(client.client_id);
                    // Importo dai soli servizi attaccati, non dai vecchi
                    // importo_totale/importo_mensile: il dovuto del mese (canone +
                    // attivazioni e una tantum del mese, backend/stato_pagamenti.py)
                    // o, se nel mese non c'è niente di dovuto, il canone pattuito.
                    // La quota sul fatturato generato si somma quando è nota,
                    // altrimenti resta come "+ N%".
                    const canone = canoneServizi(client);
                    const base = importoDelMese(client, canone);
                    const unaTantum = client.stato_pagamenti?.mese_corrente?.una_tantum || 0;
                    const tk = tiktokFees[client.client_id];
                    const quotaRegistro = client.stato_pagamenti?.mese_corrente?.quota_percentuale;
                    const quotaNota = quotaRegistro ? quotaRegistro.importo != null : tk?.tiktok_agency_fee > 0;
                    const totale = base + quotaDaSommare(client, tk);
                    const pagato = client.ha_pagato !== false;
                    const saltato = saltatoDelMese(client);
                    // Canone saltato e niente altro di dovuto nel mese: l'importo
                    // si mostra barrato, perché non entra nel fatturato.
                    const canoneBarrato = !!saltato && !client.stato_pagamenti?.mese_corrente?.dovuto;
                    const incassato = client.stato_pagamenti?.mese_corrente?.incassato || 0;
                    const attivo = client.is_active !== false;
                    const daRinnovare = attivo && !!client.stato_pagamenti?.da_rinnovare;
                    const taskAttivi = (client.enabled_tasks || ALL_TASK_TYPES.map((t) => t.id)).length;
                    const nReparti = client.reparti?.length || 0;
                    const versoAlto = index >= lista.length - 3 && lista.length > 3;
                    return (
                      <div
                        key={client.client_id}
                        className={`${gridCls} relative py-3.5 border-t border-[#f0f0f6] transition-colors duration-200 ${selezionato ? "bg-[#f7f7fe]" : "hover:bg-[#fbfbfd]"} ${flashRiga === client.client_id ? "clienti-flash" : ""}`}
                        style={gridStyle}
                        data-testid={inattivi ? `inactive-client-row-${index}` : `client-row-${index}`}
                      >
                        {/* Velo rosso del contratto da rinnovare: assoluto, quindi
                            fuori dalle celle della griglia, e senza puntatore per
                            non bloccare i comandi della riga. */}
                        {daRinnovare && (
                          <div
                            className="absolute inset-0 pointer-events-none bg-[rgba(192,57,43,.07)] border-l-[3px] border-l-[#c0392b]"
                            aria-hidden="true"
                            data-testid={`velo-da-rinnovare-${client.client_id}`}
                          />
                        )}
                        {/* 1. Selezione */}
                        {isPM && (
                          <span className="pt-0.5 md:pt-0 flex">
                          <CasellaSelezione
                            stato={selezionato}
                            onClick={() => toggleClientSelection(client.client_id)}
                            label={`Seleziona ${client.name}`}
                          />
                          </span>
                        )}

                        {/* 2. Cliente: nome, servizi, scadenza */}
                        <div className="min-w-0 flex-1 flex flex-col gap-1.5">
                          <div className="flex items-center gap-2 min-w-0 flex-wrap md:flex-nowrap">
                            <button
                              onClick={() => navigate(`/scheda-cliente/${client.client_id}`)}
                              className="text-[14px] font-bold text-[#15161c] hover:text-[#5b5bd6] text-left truncate transition-colors"
                              data-testid={`client-name-link-${client.client_id}`}
                            >
                              {client.name}
                            </button>
                            {daRinnovare && <BadgeDaRinnovare scadenza={client.contratto_data_scadenza} testId={`da-rinnovare-${client.client_id}`} />}
                            <BadgeArretrati arretrati={client.stato_pagamenti?.arretrati} testId={`arretrati-${client.client_id}`} />
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {isSMM ? (
                              client.percentuale_crescita != null ? (
                                <span className="h-[22px] px-2 rounded-[7px] bg-[#f3f3f8] text-[#3c414d] text-[11.5px] font-semibold inline-flex items-center">
                                  Crescita <span className="scheda-font-mono ml-1">{client.percentuale_crescita}%</span>
                                </span>
                              ) : null
                            ) : (
                              <>
                                {client.servizi_reparto?.gestione_meta_tiktok && (
                                  <span className="h-[22px] px-2 rounded-[7px] text-[11.5px] font-semibold inline-flex items-center gap-1" style={{ background: "#e6efff", color: "#2f5fd0" }}>
                                    Social
                                    {(isAdmin || isAMM || isPM) && <span className="scheda-font-mono">{fmtEuroIt(client.servizi_reparto.gestione_meta_tiktok.importo || 0)}</span>}
                                  </span>
                                )}
                                {client.servizi_reparto?.tiktok_shop && (
                                  <span className="h-[22px] px-2 rounded-[7px] text-[11.5px] font-semibold inline-flex items-center gap-1" style={{ background: "#fdeaf3", color: "#b83280" }}>
                                    TikTok
                                    {(isAdmin || isAMM || isPM) && <span className="scheda-font-mono">{fmtEuroIt(client.servizi_reparto.tiktok_shop.importo || 0)}</span>}
                                  </span>
                                )}
                                {!client.servizi_reparto?.tiktok_shop && !client.servizi_reparto?.gestione_meta_tiktok && client.pacchetto && (
                                  <span className="h-[22px] px-2 rounded-[7px] bg-[#f3f3f8] text-[#3c414d] text-[11.5px] font-semibold inline-flex items-center">{client.pacchetto}</span>
                                )}
                              </>
                            )}
                          </div>
                          <ScadenzaSegmenti client={client} />
                        </div>

                        {aCapo}
                        {/* 3. Date: girato, call, recupero */}
                        <div className="flex-1 min-w-0 flex flex-col gap-[5px] text-[12.5px]">
                          <button
                            type="button"
                            onClick={() => setGiratiClient(client)}
                            className="flex items-center gap-1.5 text-left hover:text-[#5b5bd6] transition-colors"
                            data-testid={`edit-date-btn-${index}`}
                          >
                            <Calendar className="w-[13px] h-[13px] text-[#9095a1] shrink-0" />
                            <span className="text-[#9095a1]">Girato</span>
                            {/* Il prossimo girato in programma; senza, l'ultima data girato. */}
                            {client.girato_prossimo || client.data_girato ? (
                              <span className="scheda-font-mono text-[#15161c]">{dataBreve(client.girato_prossimo || client.data_girato)}</span>
                            ) : (
                              <span className="text-[#b4b8c5]">—</span>
                            )}
                            {client.girati_prossimi > 1 && (
                              <span
                                className="h-[18px] px-1.5 rounded-full bg-[#eeeefc] text-[#3f3fb0] text-[11px] font-semibold scheda-font-mono inline-flex items-center"
                                aria-label={`altri ${client.girati_prossimi - 1} girati in programma`}
                                data-testid={`girati-altri-${index}`}
                              >
                                +{client.girati_prossimi - 1}
                              </span>
                            )}
                          </button>
                          {canViewCallColumn && canSeeCallObiettivo(client) && (
                            <button
                              type="button"
                              onClick={() => handleEditCallObiettivo(client)}
                              className="flex items-center gap-1.5 text-left hover:text-[#5b5bd6] transition-colors"
                              data-testid={`edit-call-btn-${index}`}
                            >
                              <Phone className="w-[13px] h-[13px] text-[#9095a1] shrink-0" />
                              <span className="text-[#9095a1]">Call</span>
                              {client.data_call_obiettivo ? (
                                <span className="scheda-font-mono text-[#15161c]">{dataBreve(client.data_call_obiettivo)}</span>
                              ) : (
                                <span className="text-[#5b5bd6] font-semibold">Imposta</span>
                              )}
                            </button>
                          )}
                          {isPM && (
                            <button
                              type="button"
                              onClick={() => handleEditGiratoRecupero(client)}
                              className="flex items-center gap-1.5 text-left hover:text-[#5b5bd6] transition-colors"
                              data-testid={`edit-recupero-btn-${index}`}
                            >
                              <RotateCcw className="w-[13px] h-[13px] text-[#9095a1] shrink-0" />
                              <span className="text-[#9095a1]">Recupero</span>
                              {client.has_girato_recupero ? (
                                <span className="scheda-font-mono font-semibold text-[#d97706]">{dataBreve(client.data_girato_recupero) || "Sì"}</span>
                              ) : (
                                <span className="text-[#9095a1]">No</span>
                              )}
                            </button>
                          )}
                        </div>

                        {/* 4. Team */}
                        {canEditAdmin && (
                          <div>
                            <TeamPill
                              client={client}
                              liste={liste}
                              versoAlto={versoAlto}
                              bump={bumpTeam?.clientId === client.client_id ? bumpTeam.ruolo : null}
                              onAssegna={(ruolo, valore) => assegnaTeam(client, ruolo, valore)}
                              testId={`team-pill-${index}`}
                            />
                          </div>
                        )}

                        {/* 5. € Mensile + pagato */}
                        {canEditAdmin && (
                          <div className="flex flex-col items-end gap-1">
                            <span
                              className="scheda-font-mono text-[14.5px] font-semibold"
                              style={{ color: totale && !canoneBarrato ? "#15161c" : "#b4b8c5" }}
                              data-testid={`client-total-${client.client_id}`}
                            >
                              {canoneBarrato && totale ? (
                                <InfoTooltip title="Mensile saltato" description={`${descrizioneSalto(saltato)}. Il canone di ${fmtEuroIt(canone.fisso)} non entra nel fatturato del mese.`}>
                                  <span className="line-through">{fmtEuroIt(totale)}</span>
                                </InfoTooltip>
                              ) : totale && unaTantum > 0 ? (
                                // Mese con attivazioni o una tantum: il dettaglio sta nel
                                // tooltip, come per "Task 6/6", così la colonna non si allarga.
                                <InfoTooltip title="Dovuto del mese" description={`${fmtEuroIt(base)} dovuti questo mese, di cui ${fmtEuroIt(unaTantum)} fra attivazioni e una tantum. Canone pattuito: ${fmtEuroIt(canone.fisso)}/mese.`}>
                                  <span className="underline decoration-dotted underline-offset-2">{fmtEuroIt(totale)}</span>
                                </InfoTooltip>
                              ) : totale ? fmtEuroIt(totale) : (!quotaNota && canone.percentuale > 0 ? "" : "—")}
                              {!quotaNota && canone.percentuale > 0 && (
                                <span className="text-[12px] text-[#6b7280]">{totale ? " + " : ""}{formatNumber(canone.percentuale)}%</span>
                              )}
                            </span>
                            {/* Senza canone (es. solo attivazione) il mensile resta vuoto:
                                l'incassato del mese dice comunque quanto ha pagato. */}
                            {!totale && incassato > 0 && (
                              <span className="text-[11px] text-[#0f7040] whitespace-nowrap">
                                <span className="scheda-font-mono font-semibold">{fmtEuroIt(incassato)}</span> incassati
                              </span>
                            )}
                            {tk?.tiktok_agency_fee > 0 && (
                              <InfoTooltip title="Fee TikTok Shop" description={`Canone ${fmtEuroIt(base)} più ${fmtEuroIt(tk.tiktok_agency_fee)} di fee sul fatturato TikTok.`}>
                                <span className="text-[11px] text-[#b83280] whitespace-nowrap">+ TikTok {fmtEuroIt(tk.tiktok_agency_fee)}</span>
                              </InfoTooltip>
                            )}
                            <InfoTooltip
                              title={saltato ? "Mensile saltato" : pagato ? "Pagato questo mese" : "Non pagato questo mese"}
                              description={saltato ? `${descrizioneSalto(saltato)}. Tocca per aprire i pagamenti.` : `${client.stato_pagamenti?.gestito
                                ? `Dai pagamenti registrati: ${client.stato_pagamenti.mese_corrente.pagate} di ${client.stato_pagamenti.mese_corrente.dovute} voci del mese${incassato > 0 ? `, ${fmtEuroIt(incassato)} incassati` : ""}.`
                                : "Cliente senza servizi nel registro pagamenti: lo stato si imposta a mano."} Tocca per registrare.`}
                            >
                              <button
                                type="button"
                                onClick={() => apriRegistraPagamenti(client.client_id)}
                                onPointerEnter={() => precaricaPagamenti(client.client_id)}
                                onFocus={() => precaricaPagamenti(client.client_id)}
                                className="h-[22px] px-2 rounded-full text-[11.5px] font-semibold inline-flex items-center gap-1.5 cursor-pointer transition-[filter,transform] duration-200 hover:brightness-95 active:scale-95 motion-reduce:transition-none"
                                style={saltato ? STILE_SALTATO : pagato ? { background: "#e7f7ee", color: "#0f7040" } : { background: "#fdecea", color: "#c0392b" }}
                                data-testid={`pagato-tag-${client.client_id}`}
                              >
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: saltato ? "#9095a1" : pagato ? "#10b981" : "#e05a4a" }} />
                                {saltato ? "Saltato" : pagato ? "Pagato" : "Non pagato"}
                              </button>
                            </InfoTooltip>
                          </div>
                        )}

                        {/* 5b. Fatturazione: scadenza e fattura del mese */}
                        {isAMM && aCapo}
                        {isAMM && (
                          <div className="basis-full md:basis-auto min-w-0">
                          <ColonnaFatturazione
                            client={client}
                            canEdit={puoImpostareScadenza(user)}
                            onAggiorna={(fattura) => setClients(prev => prev.map(c => (c.client_id === client.client_id ? { ...c, fattura_mese: fattura } : c)))}
                            testId={`fatturazione-${client.client_id}`}
                          />
                          </div>
                        )}

                        {aCapo}
                        {/* 6. Stato + task: su mobile in riga, accanto alle azioni */}
                        {canCambiareStato && (
                          <div className="flex flex-row md:flex-col items-center md:items-start gap-3 md:gap-1.5">
                            <button
                              type="button"
                              onClick={() => setClienteDaCambiareStato(client)}
                              className="h-[26px] px-2.5 rounded-full border text-[12px] font-semibold inline-flex items-center gap-1.5 transition-[background-color,color,border-color,transform] duration-[250ms] active:scale-95 motion-reduce:transition-none"
                              style={attivo
                                ? { background: "#e7f7ee", color: "#0f7040", borderColor: "#bfe8d2" }
                                : { background: "#f3f3f8", color: "#6b7280", borderColor: "#e4e4ed" }}
                              data-testid={inattivi ? `reactivate-btn-${index}` : `toggle-active-btn-${index}`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: attivo ? "#10b981" : "#b4b8c5" }} />
                              {attivo ? "Attivo" : "Inattivo"}
                            </button>
                            {isPM && (
                              <span className="flex items-center gap-1 text-[11.5px]" style={{ color: taskAttivi < nTask ? "#b45309" : "#9095a1" }}>
                                <ListChecks className="w-3.5 h-3.5" />
                                Task
                                <button
                                  type="button"
                                  onClick={() => handleEditEnabledTasks(client)}
                                  className="scheda-font-mono font-semibold underline decoration-dotted underline-offset-2 hover:text-[#5b5bd6]"
                                  data-testid={`edit-tasks-btn-${index}`}
                                >
                                  {taskAttivi}/{nTask}
                                </button>
                              </span>
                            )}
                          </div>
                        )}

                        {/* 7. Azioni */}
                        <div className="ml-auto md:ml-0 flex items-center justify-end gap-1">
                          <InfoTooltip title="Follower" description="Aggiorna i follower del cliente per piattaforma.">
                            <button
                              type="button"
                              onClick={() => handleEditFollowerStats(client)}
                              className={`${iconBtn} text-[#9b3fd6] hover:bg-[#f5ecfc]`}
                              aria-label="Follower"
                              data-testid={`follower-btn-${index}`}
                            >
                              <TrendingUp className="w-4 h-4" />
                            </button>
                          </InfoTooltip>
                          {isPM && (
                            <InfoTooltip title="Gestione reparti" description={`${nReparti} ${nReparti === 1 ? "reparto attivo" : "reparti attivi"}: servizi e importi per reparto.`}>
                              <button
                                type="button"
                                onClick={() => handleEditServizi(client)}
                                className={`${iconBtn} gap-0.5 w-auto px-1.5 hover:bg-[#e4f6f3]`}
                                style={{ color: nReparti ? "#0d8377" : "#b4b8c5" }}
                                aria-label="Gestione reparti"
                                data-testid={`servizi-btn-${index}`}
                              >
                                <Package className="w-4 h-4" />
                                <span className="scheda-font-mono text-[11.5px] font-semibold">{nReparti}</span>
                              </button>
                            </InfoTooltip>
                          )}
                          {canEditAdmin && (
                            <InfoTooltip title="Modifica dati" description="Nome, importo mensile, contratto e stato del pagamento.">
                              <button
                                type="button"
                                onClick={() => handleEditAdminData(client)}
                                className={`${iconBtn} text-[#565d6d] hover:bg-[#f3f3f8]`}
                                aria-label="Modifica dati"
                                data-testid={`edit-admin-btn-${index}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            </InfoTooltip>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className={`${iconBtn} border border-[#e0e0ea] text-[#565d6d] hover:bg-[#f3f3f8]`}
                                aria-label="Altre azioni"
                                data-testid={`more-btn-${index}`}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-[220px] p-1.5 rounded-[14px] border-[#e9e9f1] scheda-font-ui clienti-pop"
                              style={{ boxShadow: SHADOWS.flottante }}
                            >
                              <DropdownMenuItem onSelect={() => navigate(`/scheda-cliente/${client.client_id}`)} className="h-[34px] gap-2.5 rounded-[8px] text-[13px] font-medium">
                                <ExternalLink className="w-4 h-4 text-[#9095a1]" /> Apri scheda cliente
                              </DropdownMenuItem>
                              {isPM && (
                                <DropdownMenuItem onSelect={() => handleEditEnabledTasks(client)} className="h-[34px] gap-2.5 rounded-[8px] text-[13px] font-medium">
                                  <ListChecks className="w-4 h-4 text-[#9095a1]" /> Task abilitati
                                </DropdownMenuItem>
                              )}
                              {isPM && (
                                <DropdownMenuItem onSelect={() => handleEditGiratoRecupero(client)} className="h-[34px] gap-2.5 rounded-[8px] text-[13px] font-medium">
                                  <RotateCcw className="w-4 h-4 text-[#9095a1]" /> Girato di recupero
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onSelect={() => setGiratiClient(client)} className="h-[34px] gap-2.5 rounded-[8px] text-[13px] font-medium">
                                <Calendar className="w-4 h-4 text-[#9095a1]" /> Modifica data girato
                              </DropdownMenuItem>
                              {isAdmin && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onSelect={() => setDeletingClient(client)}
                                    className="h-[34px] gap-2.5 rounded-[8px] text-[13px] font-medium text-[#c0392b] focus:text-[#c0392b] focus:bg-[#fdecea]"
                                    data-testid={`delete-btn-${index}`}
                                  >
                                    <Trash2 className="w-4 h-4" /> Elimina cliente
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {isPM && (
        <BarraSelezione
          n={selectedClients.length}
          smmUsers={smmUsers}
          onAssegna={handleBulkAssign}
          onDeseleziona={() => setSelectedClients([])}
        />
      )}






      {/* Conferma del passaggio Attivo ↔ Inattivo */}
      <AlertDialog open={!!clienteDaCambiareStato} onOpenChange={(o) => { if (!o && !cambioStatoSaving) setClienteDaCambiareStato(null); }}>
        <AlertDialogContent className={DIALOG_CONTENT_CLS} data-testid="cambio-stato-cliente-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#15161c] flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#b45309] shrink-0" />
              {clienteDaCambiareStato?.is_active !== false
                ? `Rendere inattivo ${clienteDaCambiareStato?.name}?`
                : `Riattivare ${clienteDaCambiareStato?.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {clienteDaCambiareStato?.is_active !== false
                ? "Il cliente passa fra gli Inattivi: non riceverà più nuove task e sparirà da scadenze, call obiettivo e riepiloghi operativi. I dati e lo storico restano, e potrai riattivarlo in qualsiasi momento."
                : "Il cliente torna fra gli Attivi: rientrerà nella generazione delle task, nelle scadenze e nei riepiloghi operativi del team."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cambioStatoSaving}>Annulla</AlertDialogCancel>
            <button
              className={PRIMARY_BTN}
              onClick={() => handleToggleClientActive(clienteDaCambiareStato.client_id)}
              disabled={cambioStatoSaving}
              data-testid="conferma-cambio-stato-btn"
            >
              {cambioStatoSaving
                ? "Salvo..."
                : clienteDaCambiareStato?.is_active !== false ? "Rendi inattivo" : "Riattiva"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Client Confirmation Dialog */}
      <Dialog open={!!deletingClient} onOpenChange={(open) => !open && setDeletingClient(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Elimina Cliente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Stai per eliminare definitivamente il cliente <strong>{deletingClient?.name}</strong>.
              Questa azione eliminerà anche tutte le task e i dati associati.
            </p>
            <p className="text-sm font-medium">
              Per confermare, digita il nome del cliente:
            </p>
            <Input
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              placeholder={deletingClient?.name}
              className="border-red-200"
              data-testid="delete-confirm-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setDeletingClient(null); setDeleteConfirmName(""); }}>
              Annulla
            </Button>
            <Button 
              onClick={handleDeleteClient}
              disabled={deleting || deleteConfirmName !== deletingClient?.name}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {deleting ? "Eliminazione..." : "Elimina Cliente"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ModaleRegistraPagamenti
        user={user}
        // Solo la riga del cliente: ricaricare la lista intera dopo ogni
        // pagamento rendeva la registrazione lenta.
        onAggiornaCliente={(id, patch) => setClients(prev => prev.map(c => (c.client_id === id ? { ...c, ...patch } : c)))}
      />
    </div>
  );
}
